import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useRegistrationForm, type RegistrationStep } from '@/hooks/useRegistrationForm';
import { useToast } from '@/contexts/ToastContext';
import { DollarSign, Banknote, CreditCard, CheckCircle, AlertCircle, Loader2, Users, Clock, Lock } from 'lucide-react';
import { PartnerSearchCombobox } from './PartnerSearchCombobox';
import { BuddyGroupMemberPartnerSelect } from './BuddyGroupMemberPartnerSelect';
import { BuddyGroupPanel } from './BuddyGroupPanel';
import { NonMemberNotice } from './NonMemberNotice';
import { MembershipChoicePanel } from './MembershipChoicePanel';
import {
  PerformancePreferenceStep,
  type PerformanceChoice,
} from './PerformancePreferenceStep';
import type { PartnerSearchResult } from '@/api/extensions/usePartnerSearch';
import { formatCurrency } from '@/utils/formatters';
import { formatInTimezone } from '@/utils/timezone';
import { formatPreferenceParts } from '@/utils/format';
import { Input } from '@/components/ui/input';
import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/state_management_and_stores/auth.store';
import {
  StripeCheckoutDialog,
  type DeferredStripeQuote,
} from './StripeCheckoutDialog';
import { getMyGroup } from '@/services/buddy-group.service';
import type { BuddyGroupDetail } from '@/types/buddy-groups';
import { isTeamRopingEvent } from '@/utils/eventTypes';
import { resolveRegistrationPaymentVisibility } from '@/utils/registrationPaymentOptions';
import {
  maxEntriesPerPosition,
  describeBalancedSplit,
} from '@/utils/balancedPositions';
import { httpClient } from '@/api/http-client';
import {
  useBuddyGroupRegistrationStatus,
  useEventRegistrationBlock,
  useMyRegistrations,
  useMintIntent,
  useEventCredit,
} from '@/api/extensions/useRegistrationOps';
import { OrgOutstandingBalanceDialog } from './OrgOutstandingBalanceDialog';

const REQUIRED_PROFILE_FIELDS = [
  'first_name', 'last_name', 'phone', 'address1',
  'city', 'state', 'zip', 'date_of_birth', 'gender',
] as const;

interface RegistrationModalProps {
  open: boolean;
  onClose: () => void;
  eventId: string;
  event?: {
    entry_fee?: number | null;
    transaction_fee?: number | null;
    rodeo_id?: string | null;
    /**
     * Discriminator copied from the parent rodeo. Callers should merge
     * `parentJackpot.rodeo_or_jackpot` onto the event prop so the wizard
     * can drop the handicap step for rodeo events (handicaps are
     * jackpot-only).
     */
    rodeo_or_jackpot?: string | null;
    buddy_groups_enabled?: boolean;
    [key: string]: unknown;
  };
  /** Positions the user already has active registrations for */
  registeredPositions?: Set<string>;
  /**
   * The viewer's own active (non-cancelled) registration count for this
   * event. When `event.allow_multiple_registrations` is on and this hits
   * `event.max_registrations_per_user`, the modal blocks the user from
   * picking themselves as the registration target — the backend would
   * reject anyway with MAX_REGISTRATIONS_REACHED, but blocking client-
   * side avoids walking the user through the entire wizard for nothing.
   */
  actorActiveRegistrationCount?: number;
  /**
   * Per-position counts of the viewer's own active registrations.
   * Keys are "Header" / "Heeler" / "" (solo); missing keys mean zero.
   * Used for the position-picker hint text (e.g. "1 of 2 entered" on
   * a single side); the at-cap gate itself runs off the total count
   * across both sides — Header and Heeler share one budget under the
   * team-roping total-entries cap.
   */
  actorPositionCounts?: Record<string, number>;
  onSuccess?: (registration: any) => void;
}

const STEP_LABELS: Record<RegistrationStep, string> = {
  // 'partner' is the catch-all first step now: position, partner, buddy
  // group, and performance preferences all share it. Solo disciplines
  // hide position/partner sub-content but still see preferences here.
  partner: 'Entry Setup',
  performance: 'Performance Preference',
  handicap: 'Handicap',
  payment: 'Payment',
  review: 'Review & Submit',
};

export function RegistrationModal({
  open,
  onClose,
  eventId,
  event,
  registeredPositions,
  actorActiveRegistrationCount = 0,
  actorPositionCounts,
  onSuccess,
}: RegistrationModalProps) {
  const navigate = useNavigate();
  const profile = useAuthStore((s) => s.profile);
  // Header/Heeler is a team-roping concept. Solo disciplines (barrels,
  // breakaway, bareback, etc.) hide the role picker, suppress partner
  // selection, and submit registrations without a position value.
  const isTeamRoping = isTeamRopingEvent(event);
  const [selectedPartner, setSelectedPartner] = useState<PartnerSearchResult | null>(null);
  const { showSuccess, showError, showInfo } = useToast();

  // ─── Per-org outstanding balance preflight ─────────────────────────────
  // Before showing the registration wizard at all, ask the backend
  // authoritatively whether the user is blocked from registering for
  // this event by an outstanding balance with its organization. The
  // backend does the same event → rodeo → primary_organization_id
  // resolution that the on-submit gate does (with the same
  // trig_organization_name fallback for legacy rows), so the preflight
  // and on-submit paths can never disagree.
  //
  // Only fires while `open` is true so closed modals don't cost a
  // network request. The query is keyed under
  // `registrationOpsKeys.all` so it auto-invalidates after a bulk-pay
  // capture clears the user's balance.
  const { data: registrationBlock } = useEventRegistrationBlock(
    eventId,
    open,
  );

  // Snapshot the preflight result on first non-loading read for this
  // open cycle. Without this, clicking "Pay & Confirm Registration"
  // creates the in-flight `pending_payment` registration; the
  // useRegisterWithPayment.onSuccess invalidates registrationOpsKeys.all,
  // which retriggers this exact query; the refetch sees the row we just
  // created as outstanding balance; and the modal switches to the
  // OrgOutstandingBalanceDialog before the Stripe form ever mounts.
  // The snapshot resets on close, so a fresh check happens on next open.
  const [orgPreflightSnapshot, setOrgPreflightSnapshot] = useState<{
    blocked: boolean;
    organization_name: string | null;
    unpaid_events: NonNullable<typeof registrationBlock>['unpaid_events'];
    manual_charges: NonNullable<typeof registrationBlock>['manual_charges'];
  } | null>(null);

  useEffect(() => {
    if (!open) {
      setOrgPreflightSnapshot(null);
      return;
    }
    if (registrationBlock !== undefined && orgPreflightSnapshot === null) {
      setOrgPreflightSnapshot({
        blocked: !!registrationBlock.blocked,
        organization_name: registrationBlock.organization_name ?? null,
        unpaid_events: registrationBlock.unpaid_events ?? [],
        manual_charges: registrationBlock.manual_charges,
      });
    }
  }, [open, registrationBlock, orgPreflightSnapshot]);

  const orgPreflightActive = !!orgPreflightSnapshot?.blocked;
  const orgPreflightUnpaid = orgPreflightSnapshot?.unpaid_events ?? [];
  const orgPreflightManual = orgPreflightSnapshot?.manual_charges;
  const orgPreflightOrgLabel =
    orgPreflightSnapshot?.organization_name?.trim() || null;

  const isProfileComplete = useMemo(() => {
    if (!profile) return false;
    const p = profile as unknown as Record<string, unknown>;
    return REQUIRED_PROFILE_FIELDS.every((field) => {
      const val = p[field];
      return val !== undefined && val !== null && val !== '';
    });
  }, [profile]);

  // Mixed gender: determine opposite gender for partner filter
  const partnerGenderFilter = useMemo(() => {
    if (!event?.is_mixed_gender || !profile) return undefined;
    const userGender = ((profile as any).gender as string | undefined)?.toLowerCase();
    if (userGender === 'male') return 'female';
    if (userGender === 'female') return 'male';
    return undefined;
  }, [event?.is_mixed_gender, profile]);

  // Century jackpot: compute minimum partner age so combined age >= 100
  const centuryMinPartnerAge = useMemo(() => {
    if (!event?.is_century_jackpot || !profile) return undefined;
    const dob = (profile as any).date_of_birth as string | undefined;
    if (!dob) return undefined;
    const today = new Date();
    const birth = new Date(dob);
    let userAge = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      userAge--;
    }
    const minAge = 100 - userAge;
    return minAge > 0 ? minAge : 1;
  }, [event?.is_century_jackpot, profile]);

  const {
    formData,
    currentStep,
    currentStepIndex,
    steps,
    errors,
    submitError,
    isSubmitting,
    handicapCheck,
    handicapCheckLoading,
    maxHandicap,
    totalFee,
    surcharge,
    effectiveTransactionFee,
    partnerTotalFee,
    partnerFeePreview,
    targetFeePreview,
    isFreeEvent,
    policyData,
    cashAllowed,
    payLaterAllowed,
    orgUnpaidBlock,
    updateField,
    nextStep,
    prevStep,
    submit,
    createPayPalRegistration,
    completePayPalPayment,
    abandonPayPalRegistration,
    clearOrgUnpaidBlock,
    reset,
  } = useRegistrationForm(eventId, event);

  // Finals rodeos seat every contestant in every performance, so neither
  // buddy groups nor performance preferences apply. The policy endpoint is
  // the single source of truth for the flag; absent (anonymous/stale
  // payload) it degrades to the regular-rodeo behavior.
  const isFinalsRodeo = policyData?.is_finals === true;

  // Ref to track registration_id between PayPal create and approve callbacks
  const paypalRegistrationIdRef = useRef<string | null>(null);

  // True between createOrder firing and the SDK calling back into approve /
  // cancel / error. Used to lock the Back button while the embedded card
  // form (or Stripe checkout dialog) is open — without this, the user can
  // navigate back to a previous step mid-checkout, lose their Stripe
  // session, and strand a pending registration row.
  const [paypalFlowInFlight, setPaypalFlowInFlight] = useState(false);

  // Drives the Stripe Elements modal that mounts on the review step's
  // right column when the user clicks "Pay & Confirm Registration".
  const [stripeDialogOpen, setStripeDialogOpen] = useState(false);
  // Tracks the deferred-mint quote so the dialog header can read
  // total_amount + totals_by_method for display. The actual Stripe PI
  // is minted at submit time via /mint-intent (see confirmStripeIntent
  // below); no client_secret exists at quote time. Re-fetched via
  // quoteStripeOrder every time the dialog opens.
  const [stripeOrder, setStripeOrder] = useState<
    (DeferredStripeQuote & { registration_id: string }) | null
  >(null);

  // Surfaced when a non-member clicks one of the locked Pay Later / Cash
  // payment buttons. Explains the restriction and offers a CTA out to the
  // org's membership signup page (or general website fallback) instead of
  // silently swallowing the click. Driven by the locked-button onClick.
  const [membersOnlyDialogOpen, setMembersOnlyDialogOpen] = useState(false);

  // If the policy resolves the user as a non-member after they had pre-
  // selected a members-only payment method (race during initial load, or
  // stale form state from a previous open), reset to PayPal so the
  // submit button doesn't dead-end against the backend's 403 gate.
  useEffect(() => {
    if (!policyData) return;
    // Pay Later switched off for this rodeo/event — clear it regardless of
    // membership. Unlike the members-only gate below, no membership status
    // can make this selection valid.
    if (!payLaterAllowed && formData.payment_method === 'pay_later') {
      updateField('payment_method', '');
      return;
    }
    if (!policyData.member_fee_enabled || policyData.is_member) {
      return;
    }
    if (
      formData.payment_method === 'pay_later' ||
      formData.payment_method === 'cash'
    ) {
      updateField('payment_method', '');
    }
  }, [policyData, payLaterAllowed, formData.payment_method, updateField]);

  /**
   * If the user is registering with a buddy group whose primary cardholder
   * has already locked rodeo-level performance preferences, surface those
   * preferences here so the form can:
   *   - render the preference fields disabled with a "set by buddy group
   *     primary" hint, and
   *   - auto-populate the values so the contestant cannot accidentally
   *     submit something different (the backend rejects mismatches anyway).
   *
   * Only fires when buddy_group_id is set and the modal is open. Empty
   * effect when there is no rodeo or no group selected.
   */
  const [buddyGroupForRodeo, setBuddyGroupForRodeo] =
    useState<BuddyGroupDetail | null>(null);
  useEffect(() => {
    const rodeoId = (event as any)?.rodeo_id as string | undefined | null;
    // Finals rodeos have no buddy groups — skip the lookup so every
    // downstream affordance (auto-attach, decoupled-pair notices, the
    // on-behalf member picker) stays hidden even if the contestant owns a
    // group created before the rodeo was flagged as finals.
    if (!open || !rodeoId || isFinalsRodeo) {
      setBuddyGroupForRodeo(null);
      return;
    }
    let cancelled = false;
    getMyGroup(rodeoId)
      .then((g) => {
        if (!cancelled) setBuddyGroupForRodeo(g);
      })
      .catch(() => {
        if (!cancelled) setBuddyGroupForRodeo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, (event as any)?.rodeo_id, isFinalsRodeo]);

  // Fetch the rodeo's performance schedule so the preference dropdowns can
  // show each option with its date/time (e.g. "Performance 1 — Fri, May 15,
  // 6:00 PM") instead of just the bare name. Mirrors the option-building
  // logic on BuddyGroupSetupPage so the two pickers stay consistent — same
  // de-dupe rule, same `[<isoDatetime>]` value suffix that lets the backend
  // round-trip distinct rows that share a name.
  const [performanceOptions, setPerformanceOptions] = useState<
    PerformanceChoice[] | null
  >(null);
  // Pin the perf-time format to the parent rodeo's saved timezone so the
  // dropdown labels match what the host configured. Falls back to Pacific
  // (the column's DB default) when policy data hasn't loaded yet, so an
  // unauthenticated viewer or first-paint flash still renders sensibly.
  const perfDropdownTz =
    (policyData as any)?.timezone || 'America/Los_Angeles';
  useEffect(() => {
    const rodeoId = (event as any)?.rodeo_id as string | undefined | null;
    if (!open || !rodeoId) {
      setPerformanceOptions(null);
      return;
    }
    let cancelled = false;
    httpClient
      .get<{
        data: Array<{
          id: string;
          performance_name: string | null;
          is_slack: boolean | null;
          start_datetime: string | null;
        }>;
      }>(`/performances?trig_rodeo_id=${rodeoId}&limit=50`)
      .then((res) => {
        if (cancelled) return;
        const rows = res?.data ?? [];
        const seen = new Set<string>();
        const opts: PerformanceChoice[] = [...rows]
          .filter((p) => !!p.performance_name)
          .sort((a, b) => {
            const aTime = a.start_datetime
              ? Date.parse(a.start_datetime)
              : 0;
            const bTime = b.start_datetime
              ? Date.parse(b.start_datetime)
              : 0;
            return aTime - bTime;
          })
          .reduce<PerformanceChoice[]>((acc, p) => {
            const name = p.performance_name as string;
            const isSlack = !!p.is_slack;
            const isoDate = p.start_datetime ?? '';
            const dedupeKey = `${isSlack ? 'slack' : 'perf'}::${name}::${isoDate}`;
            if (seen.has(dedupeKey)) return acc;
            seen.add(dedupeKey);
            const baseValue = isSlack ? 'Slack' : name;
            const value = isoDate ? `${baseValue} [${isoDate}]` : baseValue;
            const dateLabel = isoDate
              ? formatInTimezone(isoDate, perfDropdownTz, {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: '2-digit',
                })
              : '';
            const labelName =
              isSlack && name.toLowerCase() === 'slack' ? 'Slack' : name;
            const label = dateLabel
              ? `${labelName} — ${dateLabel}`
              : labelName;
            acc.push({ value, label, isSlack });
            return acc;
          }, []);
        setPerformanceOptions(opts.length > 0 ? opts : null);
      })
      .catch(() => {
        if (!cancelled) setPerformanceOptions(null);
      });
    return () => {
      cancelled = true;
    };
    // perfDropdownTz is in deps so labels reflow once policyData hydrates the
    // rodeo's saved timezone after the initial render.
  }, [open, (event as any)?.rodeo_id, perfDropdownTz]);

  // Silent card-identity auto-lookup. Confirmed members of the rodeo's
  // primary org should have their card identity attached to the registration
  // (so they keep card-owner draw priority), but the contestant doesn't need
  // to type or even see the number — the system already knows it. Non-members
  // get nothing here; their path is the MembershipChoicePanel on the payment
  // step. This effect mirrors what `CardIdentityField` did visibly.
  useEffect(() => {
    const rodeoId = (event as any)?.rodeo_id as string | undefined | null;
    const userProfileId = (profile as any)?.id as string | undefined;
    if (!open || !rodeoId || !userProfileId) return;

    let cancelled = false;
    const lookup = async () => {
      try {
        const rodeoResponse: any = await httpClient.get(`/rodeos/${rodeoId}`);
        const orgId =
          rodeoResponse?.data?.primary_organization_id ??
          rodeoResponse?.primary_organization_id;
        if (!orgId || cancelled) return;

        const params = new URLSearchParams({
          organization_id: orgId,
          user_profile_id: userProfileId,
        });
        const membershipResponse: any = await httpClient.get(
          `/organization-memberships?${params}`,
        );
        const membership =
          membershipResponse?.data?.[0] ?? membershipResponse?.[0];
        // Backend returns `is_active` but does not filter on it (see
        // CardIdentityField for the underlying gotcha). Treat undefined as
        // active for backward compatibility with older rows.
        const isActive = membership?.is_active !== false;
        if (cancelled) return;
        if (membership?.member_number && isActive) {
          updateField('card_member_org_id', orgId);
          updateField('card_member_user_id', userProfileId);
        }
      } catch {
        // Silent failure — non-members or transient lookup error.
      }
    };
    lookup();
    return () => {
      cancelled = true;
    };
  }, [open, (event as any)?.rodeo_id, (profile as any)?.id, updateField]);

  // Hard rule: if the user is in an active/locked buddy group for this
  // rodeo, individual registration is forbidden — every event registration
  // for this rodeo must flow through the buddy-group path. Detect that
  // state here so the UI can lock buddy_group_id, hide the "Individual"
  // affordance, and surface a notice. The backend enforces the same rule
  // on submit; this just keeps the UI honest.
  const buddyGroupRequired = useMemo(() => {
    if (isFinalsRodeo || !buddyGroupForRodeo) return false;
    return (
      buddyGroupForRodeo.status === 'active' ||
      buddyGroupForRodeo.status === 'locked'
    );
  }, [isFinalsRodeo, buddyGroupForRodeo]);

  // Primary cardholder of an *active* buddy group attached to this rodeo
  // may register on behalf of any confirmed member. Locked groups are
  // excluded because the contestant list is frozen at lock time — adding
  // new event registrations on behalf of members at that point would
  // bypass the lock contract. The backend re-enforces this on every
  // submit; the UI condition below just controls whether the picker
  // renders at all.
  const myProfileId = (profile as any)?.id as string | undefined;
  const isPrimaryOfActiveGroup =
    !!buddyGroupForRodeo &&
    buddyGroupForRodeo.status === 'active' &&
    !!myProfileId &&
    buddyGroupForRodeo.primary_member_user_profile_id === myProfileId;

  // Members the primary may register: confirmed members of the group,
  // excluding pending invitations. The primary is included so the picker
  // can use a single uniform list (selecting the primary registers self).
  const registerableMembers = useMemo(() => {
    if (!isPrimaryOfActiveGroup || !buddyGroupForRodeo) return [];
    return (buddyGroupForRodeo.members ?? []).filter(
      (m) => m.invitation_status !== 'pending',
    );
  }, [isPrimaryOfActiveGroup, buddyGroupForRodeo]);

  // Pre-flight: which members of the group already have an active
  // registration for THIS event. Drives the "(already registered)"
  // suffix + disabled state in the on-behalf picker (primary only) AND
  // the buddy-group partner-picker filter (any member) so the user
  // doesn't try to pair with someone who's already in. Endpoint also
  // opportunistically backfills any solo registrations into the group
  // so members who registered before joining show up as group members.
  // Backend allows any group member to call — see
  // registration.service.ts: getBuddyGroupMemberRegistrationStatus.
  const { data: buddyGroupStatus } = useBuddyGroupRegistrationStatus(
    eventId,
    buddyGroupForRodeo?.id,
    open && !!buddyGroupForRodeo,
  );
  const alreadyRegisteredSet = useMemo(
    () => new Set(buddyGroupStatus?.registered_user_profile_ids ?? []),
    [buddyGroupStatus],
  );
  const memberRegistrationCounts = useMemo(
    () => buddyGroupStatus?.registration_counts ?? {},
    [buddyGroupStatus],
  );
  const memberPositionCountsById = useMemo(
    () => buddyGroupStatus?.position_counts_by_user ?? {},
    [buddyGroupStatus],
  );

  // If the primary lands on a target who's already registered (e.g. they
  // selected the dropdown before the status query resolved, or another
  // tab created the registration in the meantime), surface a notice and
  // block submit. Treat the empty `target_user_profile_id` as "Myself"
  // so a primary who already has their own row also gets the notice.
  //
  // Multi-registration semantics:
  //   - When `allow_multiple_registrations` is off, a single existing row
  //     is the cap → any "already registered" target gets blocked.
  //   - When it's on, the contestant may hold up to
  //     `event.max_registrations_per_user` entries. The backend's
  //     buddy-group-status endpoint returns per-member registration
  //     counts so we gate at the actual cap on every target — self
  //     uses `actorActiveRegistrationCount` (passed in by
  //     EventDetailPage), teammates use `memberRegistrationCounts`
  //     from the status query. Backend re-checks at submit either way,
  //     so this is purely UX.
  const allowMultiReg = !!(event as any)?.allow_multiple_registrations;
  const eventMaxPerUser = Math.max(
    1,
    Number((event as any)?.max_registrations_per_user ?? 1) || 1,
  );
  // Balanced Header/Heeler split. When the organizer turned it on, each side
  // has its own ceiling of ceil(cap / 2) on top of the shared total budget —
  // so a contestant with a Header already on the books can only spend their
  // remaining entry as a Heeler. Advisory only; the backend re-checks and
  // rejects with POSITION_BALANCE_REQUIRED.
  const enforceBalancedPositions =
    !!(event as any)?.enforce_balanced_positions &&
    isTeamRoping &&
    allowMultiReg &&
    eventMaxPerUser > 1;
  const perPositionCap = maxEntriesPerPosition(eventMaxPerUser);
  const effectiveTargetId =
    formData.target_user_profile_id || myProfileId || '';
  const targetIsSelf =
    !!myProfileId &&
    (!formData.target_user_profile_id ||
      formData.target_user_profile_id === myProfileId);
  const targetPositionCounts: Record<string, number> = useMemo(() => {
    if (targetIsSelf) return actorPositionCounts ?? {};
    return memberPositionCountsById[effectiveTargetId] ?? {};
  }, [
    targetIsSelf,
    actorPositionCounts,
    memberPositionCountsById,
    effectiveTargetId,
  ]);
  const targetCount = targetIsSelf
    ? actorActiveRegistrationCount
    : (memberRegistrationCounts[effectiveTargetId] ?? 0);
  // Total-entries cap: Header + Heeler share one budget on team roping
  // events, so an at-cap target is anyone whose active row count has
  // reached `eventMaxPerUser` — same rule as solo events. Single-reg
  // events (cap=1) fall through to the alreadyRegisteredSet gate
  // below.
  const targetAtCap = (() => {
    if (!allowMultiReg || eventMaxPerUser <= 1) return false;
    return targetCount >= eventMaxPerUser;
  })();
  const actorAtCap = (() => {
    if (!allowMultiReg || eventMaxPerUser <= 1) return false;
    return actorActiveRegistrationCount >= eventMaxPerUser;
  })();
  // Single-reg block: any active row hits the cap — team roping is no
  // longer the exception (1 entry total now means "Header OR Heeler,
  // not both"). The position picker reads its per-side hint counts
  // directly off `targetPositionCounts`.
  const singleRegSelfBlocked =
    targetIsSelf && alreadyRegisteredSet.has(effectiveTargetId);
  const singleRegTeammateBlocked =
    !targetIsSelf &&
    !!effectiveTargetId &&
    alreadyRegisteredSet.has(effectiveTargetId);
  const targetAlreadyRegistered =
    // Multi-reg off → block self/teammate whenever they already have
    // any active row. Header + Heeler share one budget so a single
    // existing row uses up the cap.
    (!allowMultiReg && (singleRegSelfBlocked || singleRegTeammateBlocked)) ||
    // Multi-reg on → block when the chosen target (self or teammate)
    // has hit the per-contestant cap
    (allowMultiReg && !!effectiveTargetId && targetAtCap);
  // Reference actorAtCap so the unused-locals check stays happy — it's
  // load-bearing for the count-aware copy below even though the gate
  // is now driven by the broader `targetAtCap`. Same trick for
  // `registeredPositions`: kept on the prop interface so callers can
  // continue passing it (older callers still do), but no longer
  // referenced internally now that the position picker reads its
  // hint counts off `targetPositionCounts`.
  void actorAtCap;
  void registeredPositions;

  // Eligible buddy-group teammates the user can pair with for this team
  // roping event. Drives the dropdown options inside the buddy-group
  // partner column. Excludes: pending invitations, the registrant
  // themself (actor on the self path, target on the on-behalf path),
  // and any teammate who's at the per-user entry cap.
  //
  // The cap rule is multi-reg-aware: on single-registration events
  // any existing row uses up the cap (matches the legacy behavior),
  // but on multi-reg events a teammate with quota left (e.g. 1 of 2
  // entries used) is still a valid partner — pairing them as a
  // team_partner just consumes their next slot. Without this, a
  // primary couldn't pair with a teammate who'd already entered
  // their first registration through the same group.
  const buddyGroupPartnerOptions = useMemo(() => {
    if (!isTeamRoping) return [];
    if (!buddyGroupForRodeo) return [];
    const registrantId = formData.target_user_profile_id || myProfileId || '';
    return (buddyGroupForRodeo.members ?? []).filter((m) => {
      if (m.invitation_status === 'pending') return false;
      if (registrantId && m.user_profile_id === registrantId) return false;
      if (allowMultiReg && eventMaxPerUser > 1) {
        const memberCount =
          memberRegistrationCounts[m.user_profile_id] ??
          (alreadyRegisteredSet.has(m.user_profile_id) ? 1 : 0);
        if (memberCount >= eventMaxPerUser) return false;
      } else if (alreadyRegisteredSet.has(m.user_profile_id)) {
        return false;
      }
      return true;
    });
  }, [
    isTeamRoping,
    buddyGroupForRodeo,
    myProfileId,
    formData.target_user_profile_id,
    alreadyRegisteredSet,
    memberRegistrationCounts,
    allowMultiReg,
    eventMaxPerUser,
  ]);

  // Show the "Pick from buddy group" column whenever the user is in a
  // buddy group with at least two confirmed (non-pending) members — i.e.
  // the group has potential teammates regardless of who's the entrant.
  // Why: tying visibility to `buddyGroupPartnerOptions.length > 0`
  // caused the column to disappear when the primary switched the
  // entrant on-behalf to a teammate whose only valid partner (the
  // primary themself) was already registered, even though the same
  // column rendered fine before the switch. The inner picker handles
  // its own empty state when per-entrant filters trim the list to
  // zero, so we don't lose user feedback.
  const showBuddyGroupPartnerColumn = useMemo(() => {
    if (!isTeamRoping) return false;
    if (!buddyGroupForRodeo) return false;
    const confirmedMemberCount = (buddyGroupForRodeo.members ?? []).filter(
      (m) => m.invitation_status !== 'pending',
    ).length;
    return confirmedMemberCount >= 2;
  }, [isTeamRoping, buddyGroupForRodeo]);

  // Which column of the partner picker is "active". The inactive column's
  // controls are wrapped in a `<fieldset disabled>` so keyboard / screen-
  // reader users can't tab into them. Defaults to the buddy-group dropdown
  // when the user has eligible teammates (the buddy-group flow is the
  // whole point of being here); otherwise falls back to the search column.
  const [partnerSource, setPartnerSource] = useState<'search' | 'buddy_group'>(
    'search',
  );

  // Reset to the most useful default whenever the modal opens or the set
  // of eligible buddy-group teammates changes (e.g. another tab registers
  // the last available teammate while this modal is open).
  useEffect(() => {
    if (!open) return;
    setPartnerSource(showBuddyGroupPartnerColumn ? 'buddy_group' : 'search');
  }, [open, showBuddyGroupPartnerColumn]);

  /**
   * Switch the active partner-picker column. Clears any partner already
   * selected on the OTHER side so we never submit a stale value from the
   * inactive column. Both columns write to the same form fields, so
   * blanking them and the local `selectedPartner` state covers all paths.
   */
  const switchPartnerSource = useCallback(
    (next: 'search' | 'buddy_group') => {
      if (next === partnerSource) return;
      setPartnerSource(next);
      updateField('partner_user_profile_id', '');
      updateField('partner_email', '');
      setSelectedPartner(null);
    },
    [partnerSource, updateField],
  );

  // Option A "decoupled pair" rule: a roping entry is governed by the
  // buddy group iff BOTH partners are members. When the user picks a
  // partner who is NOT in the group, the entry stays decoupled — both
  // halves get NULL buddy_group_id. See
  // docs/decisions/buddy-group-team-roping-conflict-resolution.md.
  //
  // We compute this *before* the auto-attach effect so the effect can
  // honor the rule (skip attach for out-of-group pairs, or actively
  // un-attach when the user switches partners after attach).
  const partnerIsOutOfBuddyGroup = useMemo(() => {
    if (!isTeamRoping) return false;
    if (!buddyGroupForRodeo) return false;
    const partnerId = formData.partner_user_profile_id;
    if (!partnerId) return false;
    const memberIds = new Set(
      (buddyGroupForRodeo.members ?? [])
        .filter((m) => m.invitation_status !== 'pending')
        .map((m) => m.user_profile_id),
    );
    return !memberIds.has(partnerId);
  }, [isTeamRoping, buddyGroupForRodeo, formData.partner_user_profile_id]);

  // Hard-block: a buddy-group primary cannot pair another member with a
  // partner who isn't in the same group on that member's behalf. The
  // "decoupled pair" exception (Option A) is for self-registration only —
  // only the member themselves can opt into a decoupled entry. An email-
  // only partner is treated as outside-the-group by definition since we
  // can't resolve them to a member profile.
  // Mirrors `assertActorCanRegisterMember`'s server-side guard so the user
  // gets a clear inline message instead of a 422 round-trip after submit.
  const onBehalfWithOutsidePartner = useMemo(() => {
    if (!isTeamRoping) return false;
    if (!buddyGroupForRodeo) return false;
    if (!formData.target_user_profile_id) return false;
    if (formData.target_user_profile_id === myProfileId) return false;
    if (formData.partner_email) return true;
    if (!formData.partner_user_profile_id) return false;
    return partnerIsOutOfBuddyGroup;
  }, [
    isTeamRoping,
    buddyGroupForRodeo,
    formData.target_user_profile_id,
    formData.partner_email,
    formData.partner_user_profile_id,
    myProfileId,
    partnerIsOutOfBuddyGroup,
  ]);

  const onBehalfTargetDisplayName = useMemo(() => {
    if (!formData.target_user_profile_id) return '';
    return (
      registerableMembers.find(
        (m) => m.user_profile_id === formData.target_user_profile_id,
      )?.display_name ?? 'this member'
    );
  }, [formData.target_user_profile_id, registerableMembers]);

  // Auto-attach the buddy group to the registration when the rule fires.
  // Without this, the user would have to manually pick the group from the
  // BuddyGroupPanel — defeating the point of the lockdown. The contestant
  // may still opt into picking a specific partner (registration_type =
  // 'team_primary') from inside the buddy-group flow; partner_user_profile_id
  // and buddy_group_id co-exist on the registration row.
  //
  // Decoupled exception: when the chosen partner is outside the group,
  // clear buddy_group_id so the entry stays NULL on both halves (Option
  // A). Re-attach automatically when the partner changes back to an
  // in-group member.
  useEffect(() => {
    if (!open) return;
    if (!buddyGroupRequired || !buddyGroupForRodeo) return;
    if (partnerIsOutOfBuddyGroup) {
      if (formData.buddy_group_id) {
        updateField('buddy_group_id', '');
        updateField('buddy_group_position', null);
      }
      return;
    }
    if (formData.buddy_group_id !== buddyGroupForRodeo.id) {
      updateField('buddy_group_id', buddyGroupForRodeo.id);
    }
  }, [
    open,
    buddyGroupRequired,
    buddyGroupForRodeo,
    formData.buddy_group_id,
    partnerIsOutOfBuddyGroup,
    updateField,
  ]);

  // Solo events have no partner pairing — keep registration_type pinned to
  // 'individual' and clear any stale partner state when the modal opens on
  // a non-team-roping event.
  useEffect(() => {
    if (!open || isTeamRoping) return;
    if (formData.registration_type !== 'individual') {
      updateField('registration_type', 'individual');
      updateField('partner_user_profile_id', '');
      updateField('partner_email', '');
      setSelectedPartner(null);
    }
  }, [open, isTeamRoping, formData.registration_type, updateField]);

  // Team roping is inherently a 2-person event — individual entries are not
  // allowed. Pin registration_type to 'team_primary' so every submission
  // carries a partner. The picker UI is always visible for team roping; the
  // contestant picks via search or from their buddy group.
  useEffect(() => {
    if (!open || !isTeamRoping) return;
    if (formData.registration_type !== 'team_primary') {
      updateField('registration_type', 'team_primary');
    }
  }, [open, isTeamRoping, formData.registration_type, updateField]);

  // Locked preferences inherit from the user's buddy group whenever the
  // hard rule applies (the registration is guaranteed to carry that
  // buddy_group_id). When buddy_group_id is set explicitly without the
  // rule firing — e.g. a saved/template group attached voluntarily — we
  // still honor a locked preferences set if present.
  const lockedBuddyGroupPrefs = useMemo<{
    first: string;
    second: string | null;
  } | null>(() => {
    if (!buddyGroupForRodeo) return null;
    if (!formData.buddy_group_id) return null;
    if (formData.buddy_group_id !== buddyGroupForRodeo.id) return null;
    if (!buddyGroupForRodeo.preferences_locked_at) return null;
    if (!buddyGroupForRodeo.preferences_first) return null;
    return {
      first: buddyGroupForRodeo.preferences_first,
      second: buddyGroupForRodeo.preferences_second ?? null,
    };
  }, [buddyGroupForRodeo, formData.buddy_group_id]);

  // Auto-populate the form fields when the lock kicks in. This is also
  // server-enforced — this just keeps the UI consistent with what will
  // actually be submitted.
  useEffect(() => {
    if (!lockedBuddyGroupPrefs) return;
    if (formData.first_performance_preference !== lockedBuddyGroupPrefs.first) {
      updateField('first_performance_preference', lockedBuddyGroupPrefs.first);
    }
    const desiredSecond = lockedBuddyGroupPrefs.second ?? '';
    if (formData.second_performance_preference !== desiredSecond) {
      updateField('second_performance_preference', desiredSecond);
    }
  }, [
    lockedBuddyGroupPrefs,
    formData.first_performance_preference,
    formData.second_performance_preference,
    updateField,
  ]);

  // The user's last selection from another event in the SAME rodeo —
  // typically the case when they're entering multiple events at one
  // rodeo. Drives two things below:
  //   1. The form is pre-filled with these values so the common case
  //      (same picks across all of a rodeo's events) is one click.
  //   2. PerformancePreferenceStep surfaces a warning when the live
  //      selection diverges from these values, so a deliberate change
  //      is acknowledged rather than silent.
  // Buddy-group locked prefs short-circuit this entirely — those are
  // already authoritative across the rodeo.
  const rodeoIdForPrefs =
    (event as any)?.rodeo_id as string | undefined | null;
  const shouldFetchPriorRegs = open && !!rodeoIdForPrefs;
  const { data: rodeoRegistrations = [] } = useMyRegistrations(
    shouldFetchPriorRegs ? { rodeo_id: rodeoIdForPrefs! } : undefined,
    { enabled: shouldFetchPriorRegs },
  );

  const previousRodeoPreferences = useMemo<{
    first: string;
    second: string | null;
  } | null>(() => {
    if (lockedBuddyGroupPrefs) return null;
    if (!rodeoIdForPrefs) return null;
    const rows = (rodeoRegistrations as any[]) ?? [];
    // Backend orders by created_at DESC, so the first match is the most
    // recent qualifying entry. Skip the current event (we're creating
    // that registration now), skip cancelled rows, and require at least
    // one preference value to be set — a row with both blanks tells us
    // nothing to default to.
    const candidate = rows.find((r) => {
      if (!r) return false;
      if (r.event_id === eventId) return false;
      if (r.trig_status_name === 'cancelled') return false;
      const f = (r.first_performance_preference ?? '').toString().trim();
      const s = (r.second_performance_preference ?? '').toString().trim();
      return f.length > 0 || s.length > 0;
    });
    if (!candidate) return null;
    const first = ((candidate as any).first_performance_preference ?? '') as string;
    const secondRaw = ((candidate as any).second_performance_preference ?? '') as string;
    return {
      first,
      second: secondRaw ? secondRaw : null,
    };
  }, [lockedBuddyGroupPrefs, rodeoIdForPrefs, rodeoRegistrations, eventId]);

  // Pre-fill once per modal open. We only fill when the form fields are
  // empty so a user who manually cleared a value isn't fought against by
  // this effect. Resets on close so the next open picks up fresh data.
  const previousPrefillAppliedRef = useRef(false);
  useEffect(() => {
    if (!open) {
      previousPrefillAppliedRef.current = false;
      return;
    }
    if (previousPrefillAppliedRef.current) return;
    if (lockedBuddyGroupPrefs) return;
    if (!previousRodeoPreferences) return;
    if (
      formData.first_performance_preference ||
      formData.second_performance_preference
    ) {
      // The form already carries values (e.g. user typed something
      // before the prior-rodeo data finished loading). Mark as applied
      // so we don't clobber their input.
      previousPrefillAppliedRef.current = true;
      return;
    }
    if (previousRodeoPreferences.first) {
      updateField(
        'first_performance_preference',
        previousRodeoPreferences.first,
      );
    }
    if (previousRodeoPreferences.second) {
      updateField(
        'second_performance_preference',
        previousRodeoPreferences.second,
      );
    }
    previousPrefillAppliedRef.current = true;
  }, [
    open,
    previousRodeoPreferences,
    lockedBuddyGroupPrefs,
    formData.first_performance_preference,
    formData.second_performance_preference,
    updateField,
  ]);

  const handleClose = () => {
    // If a PayPal registration was minted but never captured (user closed
    // the modal mid-flow, e.g. after entering card details on PayPal's
    // side without submitting), proactively ABANDON the pending row so
    // it leaves no trace in the user's funds list — `abandon` deletes
    // the row entirely, vs `cancel` which would leave a "cancelled"
    // history entry the user never meant to create. PayPal's onCancel
    // callback only fires when the user uses PayPal's own cancel
    // affordance, not when the host modal is dismissed, so this is the
    // catch-all. Cleared on success in handlePayPalApprove so closing
    // after a real capture won't undo a confirmed registration.
    const pendingPayPalRegId = paypalRegistrationIdRef.current;
    if (pendingPayPalRegId) {
      paypalRegistrationIdRef.current = null;
      abandonPayPalRegistration(pendingPayPalRegId).catch(() => {
        // Best-effort cleanup — backend pay-now expiry will catch it
        // even if this network call fails.
      });
    }
    reset();
    setSelectedPartner(null);
    onClose();
  };

  const partnerPosition = formData.position === 'Header' ? 'Heeler' : 'Header';
  const partnerDisplayName = selectedPartner
    ? [selectedPartner.display_first_name, selectedPartner.display_last_name].filter(Boolean).join(' ') || 'Selected partner'
    : '';

  /**
   * Handle non-PayPal submit (cash / free events).
   * PayPal flow is handled entirely via PayPalButtons callbacks below.
   */
  const handleSubmit = async () => {
    const result = await submit();
    if (result.success) {
      if (result.action === 'found_existing') {
        // On-behalf flow: surface the *target's* name in the toast so the
        // primary knows exactly which member already had a registration
        // (e.g. one they made before joining the buddy group, which the
        // join/attach backfill has now linked into the group).
        if (formData.target_user_profile_id) {
          const targetName =
            registerableMembers.find(
              (m) => m.user_profile_id === formData.target_user_profile_id,
            )?.display_name ?? 'This member';
          showSuccess(
            'Already entered',
            `${targetName} already has an active entry for this event — it's now part of your buddy group.`,
          );
        } else {
          showSuccess('Already entered', `You have an active entry as ${formData.position}`);
        }
        onSuccess?.(formData);
        handleClose();
      } else if (result.action === 'cash_pending') {
        showSuccess(
          'Entry submitted',
          'Your spot is held — pay at the event to confirm.',
        );
        onSuccess?.(formData);
        handleClose();
      } else if (result.action === 'pay_later_pending') {
        showSuccess(
          'Spot confirmed',
          'Your spot is locked in. Pay your fees online once the draw resolves.',
        );
        onSuccess?.(formData);
        handleClose();
      } else {
        showSuccess('Entry submitted');
        onSuccess?.(formData);
        handleClose();
      }
    } else if (result.error) {
      // Use the error returned directly from submit() — not the React state
      // variable (submitError), which may be stale until the next re-render.
      const errMsg = result.error;
      // The 402 UNPAID_BALANCE path raises its own modal via
      // `orgUnpaidBlock`; skip the toast so the user sees one blocker UI.
      if (errMsg.includes('outstanding unpaid fees') ||
          errMsg.includes('UNPAID_BALANCE')) {
        // intentionally silent — modal handles it
      } else if (errMsg.includes('BUDDY_GROUP_REGISTRATION_REQUIRED') ||
          errMsg.includes('Use the buddy group registration flow')) {
        showError('Buddy Group Required', errMsg);
      } else if (errMsg.includes('ON_BEHALF_NOT_ALLOWED') ||
          errMsg.includes('Only the buddy group primary cardholder') ||
          errMsg.includes('not a member of this buddy group')) {
        showError('Cannot Enter Member', errMsg);
      } else if (errMsg.includes('PRIMARY_OUTSIDE_PARTNER_NOT_ALLOWED') ||
          errMsg.includes('only pair a buddy group member with another member')) {
        showError('Outside Partner Not Allowed', errMsg);
      } else if (errMsg.includes('already in a different buddy group')) {
        showError('Buddy Group Conflict', errMsg);
      } else if (errMsg.includes('BUDDY_GROUP_DUPLICATE_RODEO_MEMBERSHIP') || errMsg.includes('DUPLICATE_RODEO_MEMBERSHIP')) {
        showError('Buddy Group Conflict', errMsg);
      } else if (errMsg.includes('ENTRY_CLOSE_PASSED') ||
          errMsg.includes('Registrations are closed for this event')) {
        showError('Entries Closed', errMsg);
      } else if (errMsg.includes('POSITION_BALANCE_REQUIRED') ||
          errMsg.includes('PARTNER_POSITION_BALANCE_REQUIRED') ||
          errMsg.includes('must be split evenly between Header and Heeler')) {
        showError('Header / Heeler Split Required', errMsg);
      } else if (errMsg.includes('MAX_REGISTRATIONS_REACHED') ||
          errMsg.includes('maximum number of entries')) {
        showError('Entry Limit Reached', errMsg);
      } else if (errMsg.includes('PARTNER_AT_MAX_ENTRIES') ||
          errMsg.includes('cannot be invited as a partner') ||
          errMsg.includes('already entered for this event')) {
        showError('Partner Already Entered', errMsg);
      } else if (errMsg.includes('409') || errMsg.includes('Conflict')) {
        showError('Entry Conflict', errMsg);
      } else {
        showError(errMsg);
      }
    }
  };

  // --- Stripe embedded flow callbacks ---
  //
  // The mint step creates a registration in pending_payment + mints a Stripe
  // PaymentIntent. The dialog uses the returned client_secret to mount
  // <PaymentElement /> for card + ACH. On success the dialog hands us the
  // PaymentIntent id, which we POST to /complete to flip the registration
  // to confirmed (the webhook is still the canonical settle path — this is
  // the optimistic UI fast path).

  const quoteStripeOrder = useCallback(async () => {
    setPaypalFlowInFlight(true);
    try {
      const response = await createPayPalRegistration();
      paypalRegistrationIdRef.current = response.registration_id;
      if (!response.payment_id) {
        throw new Error(
          'Stripe checkout could not be initialised — no payment_id returned',
        );
      }
      const quote: DeferredStripeQuote & { registration_id: string } = {
        registration_id: response.registration_id,
        payment_id: response.payment_id,
        stripe_publishable_key: response.stripe_publishable_key,
        total_amount: response.total_amount,
        totals_by_method: response.totals_by_method,
      };
      setStripeOrder(quote);
      return quote;
    } catch (err) {
      setPaypalFlowInFlight(false);
      // Per-org outstanding-balance block surfaces its own modal via
      // `orgUnpaidBlock` from the form hook — suppress the generic
      // "Registration Error" toast in that case so the user only sees
      // ONE blocker UI instead of a toast plus a modal.
      const code = (err as { code?: string } | null)?.code;
      if (code !== 'UNPAID_BALANCE') {
        const msg = err instanceof Error ? err.message : 'Entry failed';
        showError('Entry Error', msg);
      }
      throw err;
    }
  }, [createPayPalRegistration, showError]);

  const mintIntent = useMintIntent();
  // Org credit the user can apply toward entering this event (split tender).
  const { data: eventCredit } = useEventCredit(eventId, open);
  const confirmStripeIntent = useCallback(
    async (
      paymentId: string,
      paymentMethodType: 'card' | 'us_bank_account',
      creditAmount?: number,
    ) => {
      return await mintIntent.mutateAsync({
        payment_id: paymentId,
        payment_method_type: paymentMethodType,
        credit_amount: creditAmount,
      });
    },
    [mintIntent],
  );

  const handleStripeApprove = useCallback(
    async (
      paymentIntentId: string,
      methodInfo: { paymentMethodType: string | null; status: string },
    ) => {
      const registrationId = paypalRegistrationIdRef.current;
      if (!registrationId) {
        setPaypalFlowInFlight(false);
        showError('Entry data lost. Please try again.');
        return;
      }
      try {
        await completePayPalPayment(registrationId, paymentIntentId);
        // Clear the ref BEFORE handleClose runs so its dismissal cleanup
        // doesn't see a "pending" registration and try to cancel the row
        // we just successfully confirmed.
        paypalRegistrationIdRef.current = null;
        setPaypalFlowInFlight(false);
        setStripeOrder(null);
        // Tailor the toast: ACH (us_bank_account) settles asynchronously
        // over 3-5 business days, so calling it "Payment successful!"
        // would be misleading. The entry IS confirmed immediately, but
        // the funds are still in transit.
        const isAchProcessing =
          methodInfo.paymentMethodType === 'us_bank_account' ||
          methodInfo.status === 'processing';
        if (isAchProcessing) {
          showSuccess(
            'Entry confirmed!',
            'Bank transfer initiated — your spot is held while it clears (3-5 business days).',
          );
        } else {
          showSuccess('Payment successful!', 'Your entry is confirmed.');
        }
        onSuccess?.(formData);
        handleClose();
      } catch (err) {
        setPaypalFlowInFlight(false);
        showError(err instanceof Error ? err.message : 'Payment confirmation failed');
      }
    },
    [completePayPalPayment, showSuccess, showError, onSuccess, formData, handleClose],
  );

  const handleStripeCancel = useCallback(async () => {
    const registrationId = paypalRegistrationIdRef.current;
    if (registrationId) {
      try {
        // Abandon (delete) rather than cancel (mark cancelled) — the
        // user never paid, so a "cancelled" history entry would just
        // be confusing noise in their funds list.
        await abandonPayPalRegistration(registrationId);
      } catch {
        // Best-effort cleanup — don't block the user
      }
    }
    paypalRegistrationIdRef.current = null;
    setStripeOrder(null);
    setPaypalFlowInFlight(false);
    showInfo('Payment cancelled', 'No charges were made.');
  }, [abandonPayPalRegistration, showInfo]);

  // Per-org outstanding-balance gate: when the user has past-due unpaid
  // fees with this event's organization, show ONLY the blocker modal —
  // the registration wizard is suppressed entirely so the user can't
  // even start filling it out. They settle the balance, the
  // outstanding-balance query auto-invalidates on capture, and the
  // wizard becomes available on their next click of "Register Now."
  if (orgPreflightActive) {
    return (
      <OrgOutstandingBalanceDialog
        open={open}
        onClose={handleClose}
        organizationName={orgPreflightOrgLabel}
        unpaidEvents={orgPreflightUnpaid}
        manualCharges={
          orgPreflightManual && orgPreflightManual.blocking_net_owed > 0
            ? {
                // Only the BLOCKING charges gate entry — surface that amount +
                // ids so paying here clears exactly what unblocks the org.
                net_owed: orgPreflightManual.blocking_net_owed,
                adjustment_ids: orgPreflightManual.blocking_adjustment_ids,
                charges: orgPreflightManual.blocking_charges,
              }
            : undefined
        }
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent className="sm:max-w-md md:max-w-4xl max-h-[92vh] flex flex-col gap-4 overflow-hidden">
        <DialogHeader>
          <DialogTitle>Enter Event</DialogTitle>
        </DialogHeader>

        {!isProfileComplete ? (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 space-y-3">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-amber-500 dark:text-amber-400 mt-0.5 shrink-0" />
              <div>
                <p className="font-medium text-amber-600 dark:text-amber-400">
                  Incomplete Profile
                </p>
                <p className="text-sm text-amber-600/80 dark:text-amber-400/80 mt-1">
                  Please complete your profile before entering events.
                </p>
              </div>
            </div>
            <Button
              className="w-full"
              onClick={() => {
                handleClose();
                navigate('/profile');
              }}
            >
              Complete Profile
            </Button>
          </div>
        ) : (
          <>
        {/* Step indicator + label — stays pinned above the scrollable body */}
        <div>
          <div className="flex gap-1 mb-2">
            {steps.map((step, i) => (
              <div
                key={step}
                className={`flex-1 h-1.5 rounded-full transition-colors ${
                  i <= currentStepIndex ? 'bg-primary' : 'bg-muted'
                }`}
              />
            ))}
          </div>
          <p className="text-sm font-medium text-muted-foreground">
            Step {currentStepIndex + 1}: {STEP_LABELS[currentStep]}
          </p>
        </div>

        {/* Scrollable body — caps modal height and lets long step content
            (especially payment) flow inside an internal scroll region. */}
        <div className="flex-1 min-h-0 overflow-y-auto -mx-6 px-6 space-y-4">
          {/* Outstanding-balance gating now lives in
              <OrgOutstandingBalanceDialog/> — it pops on submit when the
              backend returns 402 UNPAID_BALANCE for the org that owns
              this event's rodeo. Cross-org balances are intentionally
              ignored here so a user owing org A can still register for
              org B's events. The dialog is rendered below as a sibling
              of the wizard. */}

        {/* Buddy-group-required notice. Shown on the first step only — once
            the user has acknowledged the lock-in by moving past Position &
            Partner, repeating the explanation on every step is just noise. */}
        {buddyGroupRequired && buddyGroupForRodeo && currentStepIndex === 0 && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-1.5 text-sm">
            <div className="flex items-center gap-2 text-primary">
              <Users className="h-4 w-4" />
              <span className="font-medium">
                Registering as a member of {buddyGroupForRodeo.group_name || 'your buddy group'}
              </span>
            </div>
            <p className="text-xs text-muted-foreground">
              You belong to a buddy group for this rodeo, so every event
              in this rodeo is registered through your group.
              {isTeamRoping && ' For team roping entries, you must select a partner below.'}
            </p>
          </div>
        )}

        {/* On-behalf member picker — only shown when the actor is the
            primary cardholder of an active buddy group attached to this
            rodeo. Confined to the first wizard step so changing the
            registrant after the user has moved past position / partner
            (and possibly invited a partner) is impossible — that would
            leave stale partner state attached to a different target. */}
        {isPrimaryOfActiveGroup &&
          buddyGroupForRodeo &&
          registerableMembers.length > 1 &&
          currentStepIndex === 0 && (
          <div className="rounded-lg border border-primary/30 bg-primary/5 p-3 space-y-2 text-sm">
            <div className="flex items-center gap-2 text-primary">
              <Users className="h-4 w-4" />
              <Label htmlFor="onbehalf-target" className="font-medium">
                Who are you registering?
              </Label>
            </div>
            <Select
              // Empty string = self (the primary). Any other value is the
              // target member's user_profile_id; we also auto-attach
              // the buddy group id so the backend's on-behalf
              // authorization passes (see `assertActorCanRegisterMember`).
              value={formData.target_user_profile_id || '__self__'}
              onValueChange={(v) => {
                if (v === '__self__') {
                  updateField('target_user_profile_id', '');
                } else {
                  updateField('target_user_profile_id', v);
                  if (formData.buddy_group_id !== buddyGroupForRodeo.id) {
                    updateField('buddy_group_id', buddyGroupForRodeo.id);
                  }
                  // Online pay (Stripe) isn't supported for on-behalf yet
                  // (one PaymentIntent per actor session is the binding
                  // constraint), so clear any stale selection. The user
                  // will pick Cash or Pay Later at the payment step.
                  if (
                    formData.payment_method === 'stripe' ||
                    formData.payment_method === 'paypal'
                  ) {
                    updateField('payment_method', '');
                  }
                  // When the primary changes targets, clear any partner
                  // selection that was made for a different target. The
                  // primary then picks a partner for the new target via
                  // the always-visible partner picker below; the on-
                  // behalf submit path forwards partner_user_profile_id
                  // along with the target_user_profile_id payload.
                  // registration_type itself isn't reset here — for team
                  // roping it's pinned to 'team_primary' by an effect,
                  // and for solo events it's pinned to 'individual'.
                  if (
                    formData.partner_user_profile_id ||
                    formData.partner_email
                  ) {
                    updateField('partner_user_profile_id', '');
                    updateField('partner_email', '');
                    setSelectedPartner(null);
                  }
                }
              }}
            >
              <SelectTrigger id="onbehalf-target">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__self__">
                  Myself
                  {profile && (() => {
                    const p = profile as any;
                    const name = [p.display_first_name ?? p.first_name, p.display_last_name ?? p.last_name]
                      .filter(Boolean)
                      .join(' ');
                    return name ? ` (${name})` : '';
                  })()}
                  {/* Myself suffix — for multi-reg events, surface the
                      "X of Y entries used" count so the primary can see
                      they're at the cap before the notice fires. Both
                      team roping and solo events share one total
                      budget (Header + Heeler don't get separate
                      pools), so the cap displayed is just the bare
                      max. Single-reg events fall back to the simple
                      "already registered" hint that mirrors the
                      teammate suffix below. */}
                  {myProfileId && alreadyRegisteredSet.has(myProfileId)
                    ? allowMultiReg && eventMaxPerUser > 1
                      ? ` — ${actorActiveRegistrationCount} of ${eventMaxPerUser} entries used`
                      : ' — already entered'
                    : ''}
                </SelectItem>
                {registerableMembers
                  .filter((m) => m.user_profile_id !== myProfileId)
                  .map((m) => {
                    const memberCount =
                      memberRegistrationCounts[m.user_profile_id] ?? 0;
                    const isRegistered = memberCount > 0;
                    // Radix's <SelectItem> can't be `disabled` and still
                    // be visible/selected, so we keep it selectable but
                    // show a per-target suffix and surface a notice +
                    // submit-block when the picked target is at cap.
                    // Multi-reg events show "X of Y entries used" so
                    // primaries see remaining quota at a glance; single-
                    // reg events keep the simpler "already registered".
                    // Header + Heeler share one budget under the team
                    // roping total-entries cap, so the cap shown for
                    // each teammate is just the bare max.
                    const suffix = !isRegistered
                      ? ''
                      : allowMultiReg && eventMaxPerUser > 1
                        ? ` — ${memberCount} of ${eventMaxPerUser} entries used`
                        : ' — already entered';
                    return (
                      <SelectItem key={m.user_profile_id} value={m.user_profile_id}>
                        {(m.display_name ?? 'Group member') + suffix}
                      </SelectItem>
                    );
                  })}
              </SelectContent>
            </Select>
            {targetAlreadyRegistered ? (
              <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                {formData.target_user_profile_id
                  ? allowMultiReg && eventMaxPerUser > 1
                    ? `This member has used all ${eventMaxPerUser} of their entries for this event. Pick a different member or close the modal.`
                    : "This member is already entered for this event — their existing entry is part of the buddy group. There's nothing to enter here. Pick a different member or close the modal."
                  : allowMultiReg && eventMaxPerUser > 1
                    ? `You've used all ${eventMaxPerUser} of your own entries for this event. Pick a buddy group member to enter them on behalf, or close the modal.`
                    : "You're already entered for this event. Pick a different member to enter on their behalf, or close the modal."}
              </p>
            ) : formData.target_user_profile_id ? (
              <p className="text-xs text-muted-foreground">
                {event?.rodeo_or_jackpot === 'rodeo'
                  ? "Entering on behalf of this buddy group member. The spot is held for them and they'll pick their own payment method when they settle the balance from their account."
                  : "Entering on behalf of this buddy group member. Their handicap on file will be used. The spot is held for them and they'll pick their own payment method when they settle the balance from their account."}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                Defaults to entering yourself. Pick a member to sign
                them up for this event with your group.
              </p>
            )}
          </div>
        )}

        <div className="space-y-4 min-h-[180px]">
          {/* Partner step */}
          {currentStep === 'partner' && (
            <div className="space-y-3">
              {/* Header/Heeler picker — team roping only. Lives at the top
                  of the partner step now that the standalone position step
                  is gone; solo disciplines skip this step entirely. */}
              {isTeamRoping && (
                <>
                  <Label>What position are you roping?</Label>
                  <div className="grid grid-cols-2 gap-3">
                    {(['Header', 'Heeler'] as const).map((pos) => {
                      // Header + Heeler share one budget under the
                      // team-roping total-entries cap, so a side is
                      // "full" once the cap as a whole is hit:
                      //   - Single-reg (1 entry total): any existing
                      //     row, on either side, takes both buttons
                      //     out of play.
                      //   - Multi-reg: same total cap applies. The
                      //     duplicate-tuple check on the backend still
                      //     stops a contestant from re-entering the
                      //     same role with the same partner — that
                      //     particular collision surfaces as a 409 on
                      //     submit rather than a disabled button here.
                      const positionCount = targetPositionCounts[pos] ?? 0;
                      const totalAtCap =
                        allowMultiReg &&
                        eventMaxPerUser > 1 &&
                        targetCount >= eventMaxPerUser;
                      const filledSingleReg =
                        !allowMultiReg && alreadyRegisteredSet.has(effectiveTargetId);
                      // Balanced-split events cap each side independently, so
                      // one end can close while the other is still open.
                      const sideAtCap =
                        enforceBalancedPositions && positionCount >= perPositionCap;
                      const alreadyRegistered =
                        filledSingleReg || totalAtCap || sideAtCap;
                      return (
                        <button
                          key={pos}
                          type="button"
                          disabled={alreadyRegistered}
                          onClick={() => updateField('position', pos)}
                          className={`rounded-lg border p-4 text-center font-medium transition-colors ${
                            alreadyRegistered
                              ? 'border-border bg-muted text-muted-foreground cursor-not-allowed opacity-60'
                              : formData.position === pos
                                ? 'border-primary bg-primary/5 text-primary'
                                : 'border-border hover:border-primary/50'
                          }`}
                        >
                          {pos}
                          {alreadyRegistered ? (
                            <span className="block text-xs mt-1">
                              {sideAtCap && !totalAtCap
                                ? `${positionCount} of ${perPositionCap} ${pos} entries used`
                                : totalAtCap
                                  ? `${positionCount} of ${eventMaxPerUser} entered`
                                  : 'Already entered'}
                            </span>
                          ) : allowMultiReg && eventMaxPerUser > 1 && positionCount > 0 ? (
                            <span className="block text-xs mt-1 text-muted-foreground">
                              {positionCount} entered
                            </span>
                          ) : null}
                        </button>
                      );
                    })}
                  </div>
                  {enforceBalancedPositions && (
                    <p className="text-xs text-muted-foreground">
                      This event requires an even Header / Heeler split —{' '}
                      {describeBalancedSplit(eventMaxPerUser)}. Once a side is
                      full you can only enter from the other end.
                    </p>
                  )}
                  {errors.position && (
                    <p className="text-xs text-destructive">{errors.position}</p>
                  )}
                </>
              )}

              {/* Partner picker — team roping is inherently a 2-person event,
                  so partner selection is mandatory and always visible. Buddy-
                  group members can pick from either the user search or the
                  buddy-group member list (when their group is attached to the
                  rodeo). On-behalf primaries see the same picker and lock in
                  a partner for the target member; the submit path forwards
                  partner_user_profile_id along with the on-behalf payload. */}
              {isTeamRoping && (
                  <>
                    <Label>Find Your Partner ({partnerPosition})</Label>
                    {showBuddyGroupPartnerColumn ? (
                      <>
                        {/* Two-column picker — radio chooses which column is
                            active. Inactive column is wrapped in a
                            `<fieldset disabled>` so its inputs / buttons /
                            Radix Select trigger are all genuinely disabled
                            (HTML semantics cascade), not just visually dimmed.
                            Switching columns clears the other side's value
                            via switchPartnerSource() so we never submit a
                            stale selection from the inactive column. */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          {/* Column A — search by name / email */}
                          <div
                            className={`rounded-lg border p-3 transition-colors ${
                              partnerSource === 'search'
                                ? 'border-primary bg-primary/5'
                                : 'border-border opacity-60'
                            }`}
                          >
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="radio"
                                name="partner-source"
                                value="search"
                                checked={partnerSource === 'search'}
                                onChange={() => switchPartnerSource('search')}
                                className="h-4 w-4 accent-primary"
                              />
                              <span className="text-sm font-medium">
                                Search for a partner
                              </span>
                            </label>
                            <fieldset
                              disabled={partnerSource !== 'search'}
                              aria-disabled={partnerSource !== 'search'}
                              className="border-0 p-0 m-0 disabled:cursor-not-allowed"
                            >
                              {/* Render with empty value when this column is
                                  inactive. selectedPartner / partner_email
                                  are shared with the buddy-group column, so
                                  passing them through unconditionally would
                                  bleed a buddy-group pick into the disabled
                                  search card (it would render its "Selected
                                  state" UI showing the buddy member's name).
                                  The fieldset only disables form controls —
                                  it doesn't suppress rendered content. */}
                              <PartnerSearchCombobox
                                value={partnerSource === 'search' ? selectedPartner : null}
                                onChange={(user) => {
                                  setSelectedPartner(user);
                                  updateField('partner_email', '');
                                  updateField(
                                    'partner_user_profile_id',
                                    user ? user.id : '',
                                  );
                                }}
                                partnerPosition={partnerPosition}
                                error={errors.partner_email}
                                filterGender={partnerGenderFilter}
                                minPartnerAge={centuryMinPartnerAge}
                                eventId={eventId}
                                // Exclude the registrant (the buddy-group
                                // teammate being registered on the on-behalf
                                // path; the actor in self-registration). This
                                // lets the actor surface as a candidate
                                // partner for the teammate they're registering
                                // — which is the whole point of being able to
                                // pair yourself with a buddy you're entering.
                                excludeUserProfileId={
                                  formData.target_user_profile_id || undefined
                                }
                              />
                            </fieldset>
                          </div>

                          {/* Column B — pick from buddy group members */}
                          <div
                            className={`rounded-lg border p-3 transition-colors ${
                              partnerSource === 'buddy_group'
                                ? 'border-primary bg-primary/5'
                                : 'border-border opacity-60'
                            }`}
                          >
                            <label className="flex items-center gap-2 cursor-pointer mb-2">
                              <input
                                type="radio"
                                name="partner-source"
                                value="buddy_group"
                                checked={partnerSource === 'buddy_group'}
                                onChange={() => switchPartnerSource('buddy_group')}
                                className="h-4 w-4 accent-primary"
                              />
                              <span className="text-sm font-medium">
                                Pick from buddy group
                              </span>
                            </label>
                            <fieldset
                              disabled={partnerSource !== 'buddy_group'}
                              aria-disabled={partnerSource !== 'buddy_group'}
                              className="border-0 p-0 m-0 disabled:cursor-not-allowed"
                            >
                              <BuddyGroupMemberPartnerSelect
                                members={buddyGroupPartnerOptions}
                                currentUserProfileId={
                                  // Exclude the registrant (target on the
                                  // on-behalf path; otherwise the actor)
                                  // from the picker — you can't be your
                                  // own partner.
                                  formData.target_user_profile_id || myProfileId
                                }
                                value={
                                  // Same mutual-exclusion concern as Column A:
                                  // when the user is searching, don't surface
                                  // the search hit as a "selected" member in
                                  // the disabled buddy-group dropdown.
                                  partnerSource === 'buddy_group'
                                    ? (selectedPartner
                                        ? selectedPartner.id
                                        : formData.partner_user_profile_id)
                                    : ''
                                }
                                onChange={(member) => {
                                  if (member) {
                                    // Synthesize a PartnerSearchResult so the
                                    // review step + selectedPartner UI keep
                                    // showing the chosen name. display_name
                                    // is a single string in the buddy-group
                                    // shape — stash it in the first-name slot
                                    // since the consumer just join()s the
                                    // two halves.
                                    setSelectedPartner({
                                      id: member.user_profile_id,
                                      display_first_name:
                                        member.display_name ?? null,
                                      display_last_name: null,
                                      first_name: null,
                                      last_name: null,
                                      primary_email: null,
                                    });
                                    updateField(
                                      'partner_user_profile_id',
                                      member.user_profile_id,
                                    );
                                    updateField('partner_email', '');
                                  } else {
                                    setSelectedPartner(null);
                                    updateField('partner_user_profile_id', '');
                                    updateField('partner_email', '');
                                  }
                                }}
                                partnerPosition={partnerPosition}
                              />
                            </fieldset>
                          </div>
                        </div>
                        {centuryMinPartnerAge != null && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                            Century event: combined age must be 100+ years. Partner must be at least {centuryMinPartnerAge} years old.
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Your partner will receive a notification to confirm.
                        </p>
                      </>
                    ) : (
                      <>
                        <PartnerSearchCombobox
                          value={selectedPartner}
                          onChange={(user) => {
                            setSelectedPartner(user);
                            updateField('partner_email', '');
                            updateField(
                              'partner_user_profile_id',
                              user ? user.id : '',
                            );
                          }}
                          partnerPosition={partnerPosition}
                          error={errors.partner_email}
                          filterGender={partnerGenderFilter}
                          minPartnerAge={centuryMinPartnerAge}
                          eventId={eventId}
                          excludeUserProfileId={
                            formData.target_user_profile_id || undefined
                          }
                        />
                        {centuryMinPartnerAge != null && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                            Century event: combined age must be 100+ years. Partner must be at least {centuryMinPartnerAge} years old.
                          </p>
                        )}
                        <p className="text-xs text-muted-foreground">
                          Your partner will receive a notification to confirm.
                        </p>
                      </>
                    )}
                    {/*
                      Option A "decoupled pair" heads-up. Fires whenever the
                      user is in a buddy group attached to the rodeo AND the
                      chosen partner is NOT in that group. The entry stays
                      decoupled (NULL buddy_group_id on both halves) and is
                      drawn outside the group's preferences. The auto-attach
                      effect already cleared buddy_group_id; this notice
                      tells the user *why* their pick won't follow group
                      preferences.
                    */}
                    {onBehalfWithOutsidePartner && buddyGroupForRodeo ? (
                      <div
                        role="alert"
                        className="text-xs rounded border border-destructive/40 bg-destructive/10 text-destructive p-3 mt-2"
                      >
                        <p className="font-medium mb-1">
                          You can't pair {onBehalfTargetDisplayName} with someone outside the buddy group.
                        </p>
                        <p>
                          As primary you can only pair a buddy group member
                          with another member of{' '}
                          <strong>
                            {buddyGroupForRodeo.group_name || 'your buddy group'}
                          </strong>
                          . If {onBehalfTargetDisplayName} wants to rope with
                          someone outside the group, they'll need to register
                          themselves — that creates a decoupled entry that
                          doesn't follow the group's preferences.
                        </p>
                      </div>
                    ) : (
                      partnerIsOutOfBuddyGroup &&
                      buddyGroupForRodeo &&
                      selectedPartner && (
                        <div
                          role="status"
                          className="text-xs rounded border border-amber-500/30 bg-amber-500/10 text-amber-900 dark:text-amber-200 p-3 mt-2"
                        >
                          <p className="font-medium mb-1">
                            Heads up — this entry won't follow your buddy group's preferences.
                          </p>
                          <p>
                            {(
                              [
                                selectedPartner.display_first_name,
                                selectedPartner.display_last_name,
                              ]
                                .filter(Boolean)
                                .join(' ') || 'Your partner'
                            )}{' '}
                            isn't in{' '}
                            <strong>
                              {buddyGroupForRodeo.group_name || 'your buddy group'}
                            </strong>
                            , so this team roping pair will be drawn separately
                            using your own preferences instead of the group's.
                          </p>
                        </div>
                      )
                    )}
                  </>
              )}

              {/* Buddy Group section — only shown when rodeo has it enabled and non-members aren't blocked.
                  Finals rodeos run every contestant in every performance, so
                  there is nothing for a group to co-schedule — the backend
                  rejects buddy groups there too. */}
              {event?.buddy_groups_enabled && event?.rodeo_id && !isFinalsRodeo &&
                !(policyData && !policyData.is_member && !policyData.allow_non_member_buddy_group) && (
                <div className="border-t pt-3 mt-3">
                  <Label className="flex items-center gap-1.5 mb-2">
                    <Users className="h-4 w-4" />
                    Buddy Group (optional)
                  </Label>
                  <BuddyGroupPanel
                    rodeoId={event.rodeo_id}
                    selectedGroupId={formData.buddy_group_id}
                    selectedPosition={formData.buddy_group_position}
                    onPositionChanged={(position) => updateField('buddy_group_position', position)}
                    onGroupSelected={(groupId) => updateField('buddy_group_id', groupId)}
                    onGroupCleared={() => {
                      if (buddyGroupRequired) return;
                      updateField('buddy_group_id', '');
                      updateField('buddy_group_position', null);
                    }}
                    multiEntryDisabled={!!(event as any).entry_limits?.allow_multiple_entries}
                    cardOwnerOrgId={formData.card_member_org_id}
                    cardOwnerUserId={formData.card_member_user_id}
                    buddyGroupRequired={buddyGroupRequired}
                  />
                </div>
              )}

              {/* Performance preferences inline at the bottom of step 1.
                  Compact layout puts the two selects side-by-side on md+ to
                  keep the step short. The lock state from a buddy group
                  primary's locked preferences greys both selects out, with
                  a one-line notice explaining where to change them. */}
              {!isFinalsRodeo && (
              <div className="border-t pt-3 mt-3 space-y-2">
                <Label className="text-sm">
                  Performance Preferences{' '}
                  <span className="font-normal text-muted-foreground">
                    (optional)
                  </span>
                </Label>
                <p className="text-xs text-muted-foreground">
                  Optional — if you don&apos;t pick, we&apos;ll assign you to
                  any performance.
                </p>
                {performanceOptions && performanceOptions.length > 0 ? (
                  <PerformancePreferenceStep
                    performanceOptions={performanceOptions}
                    firstPreference={formData.first_performance_preference}
                    secondPreference={formData.second_performance_preference}
                    onFirstChange={(v) => updateField('first_performance_preference', v)}
                    onSecondChange={(v) => updateField('second_performance_preference', v)}
                    preferenceConfig={policyData?.preference_config ?? null}
                    error={errors.first_performance_preference}
                    lockedByBuddyGroup={!!lockedBuddyGroupPrefs}
                    previousPreferences={previousRodeoPreferences}
                    timezone={perfDropdownTz}
                    compact
                  />
                ) : (
                  <PerformancePreferenceStep
                    performances={((event as any)?.named_performances as string[]) || ['Performance 1', 'Performance 2', 'Performance 3']}
                    firstPreference={formData.first_performance_preference}
                    secondPreference={formData.second_performance_preference}
                    onFirstChange={(v) => updateField('first_performance_preference', v)}
                    onSecondChange={(v) => updateField('second_performance_preference', v)}
                    preferenceConfig={policyData?.preference_config ?? null}
                    error={errors.first_performance_preference}
                    lockedByBuddyGroup={!!lockedBuddyGroupPrefs}
                    previousPreferences={previousRodeoPreferences}
                    timezone={perfDropdownTz}
                    compact
                  />
                )}
              </div>
              )}
            </div>
          )}

          {/* Handicap step */}
          {currentStep === 'handicap' && (
            <div className="space-y-3">
              {handicapCheckLoading ? (
                <div className="flex items-center gap-2 text-muted-foreground py-4">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Checking handicap...</span>
                </div>
              ) : handicapCheck && !handicapCheck.needs_handicap && handicapCheck.existing_handicap_value != null ? (
                /* User has a handicap on file */
                <div className="rounded-lg border border-green-500/30 bg-green-500/10 p-4 space-y-2">
                  <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                    <CheckCircle className="h-4 w-4" />
                    <span className="text-sm font-medium">Handicap on file</span>
                  </div>
                  <div className="grid grid-cols-2 gap-1 text-sm">
                    <span className="text-muted-foreground">Organization</span>
                    <span className="font-medium">{handicapCheck.organization_name}</span>
                    <span className="text-muted-foreground">Type</span>
                    <span className="font-medium">{handicapCheck.handicap_type_name}</span>
                    <span className="text-muted-foreground">Rating</span>
                    {handicapCheck.is_user_entered && !handicapCheck.is_approved ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min={0}
                          max={maxHandicap ?? 15}
                          step={0.5}
                          className="h-7 w-20 text-sm"
                          value={formData.manual_handicap_value ?? handicapCheck.existing_handicap_value ?? ''}
                          onChange={(e) => {
                            const val = e.target.value;
                            if (val === '') {
                              updateField('manual_handicap_value', null);
                            } else {
                              const num = parseFloat(val);
                              if (!isNaN(num)) {
                                updateField('manual_handicap_value', num);
                              }
                            }
                          }}
                        />
                      </div>
                    ) : (
                      <span className="font-medium">{handicapCheck.existing_handicap_value}</span>
                    )}
                    <span className="text-muted-foreground">Status</span>
                    <span className={`font-medium ${handicapCheck.is_approved ? 'text-green-600 dark:text-green-400' : 'text-amber-500 dark:text-amber-400'}`}>
                      {handicapCheck.is_approved ? 'Approved' : 'Pending approval'}
                    </span>
                  </div>
                  {handicapCheck.is_user_entered && !handicapCheck.is_approved && (
                    <p className="text-xs text-muted-foreground">You can edit your handicap while it's pending approval.</p>
                  )}
                </div>
              ) : handicapCheck?.needs_handicap ? (
                /* User needs to enter a handicap */
                <div className="space-y-3">
                  <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-3">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-500 dark:text-amber-400 mt-0.5 shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-600 dark:text-amber-400">
                          No handicap on file{handicapCheck.organization_name ? ` for ${handicapCheck.organization_name}` : ''}
                        </p>
                        <p className="text-amber-600/80 dark:text-amber-400/80 mt-1">
                          Enter your handicap number below. It will be submitted for approval by the organization.
                        </p>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>
                      {isTeamRoping && formData.position
                        ? `Your ${formData.position} handicap number`
                        : 'Your handicap number'}
                    </Label>
                    <Input
                      type="number"
                      min={0}
                      max={maxHandicap ?? 15}
                      step={0.5}
                      placeholder="e.g. 4"
                      value={formData.manual_handicap_value ?? ''}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '') {
                          updateField('manual_handicap_value', null);
                        } else {
                          const num = parseFloat(val);
                          if (!isNaN(num)) {
                            updateField('manual_handicap_value', num);
                          }
                        }
                      }}
                    />
                    {errors.manual_handicap_value && (
                      <p className="text-xs text-destructive mt-1">{errors.manual_handicap_value}</p>
                    )}
                  </div>
                </div>
              ) : (
                /* No handicap org configured — skip silently */
                <div className="text-sm text-muted-foreground py-4">
                  <p>No handicap requirements for this event. You're good to go!</p>
                </div>
              )}

              <div>
                <Label>Notes (optional)</Label>
                <Textarea
                  placeholder="Any notes for the event organizer..."
                  value={formData.notes}
                  onChange={(e) => updateField('notes', e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          {/* Payment step — single column on mobile. On md+ we collapse to
              one column whenever there's nothing to put in the membership
              column (confirmed members of the org, or orgs that don't
              charge a member fee at all). Otherwise we render the
              two-column layout with membership context on the left and the
              actionable money/payment column on the right. */}
          {currentStep === 'payment' && (() => {
            const showMembershipColumn =
              !!policyData && !policyData.is_member;
            const containerClass = showMembershipColumn
              ? 'space-y-4 md:space-y-0 md:grid md:grid-cols-2 md:gap-x-4 md:items-start'
              : 'space-y-4 max-w-xl mx-auto';
            return (
            <div className={containerClass}>
              {/* Left column — membership status & draw context. Only
                  rendered when there's something to show; otherwise the
                  layout collapses to a single centered column above. */}
              {showMembershipColumn && (
              <div className="space-y-4">
                {/* Non-member registrations: surface the three-path picker
                    (pay non-member fee / become a member / claim membership)
                    alongside the fee breakdown so the contestant only has to
                    think about money in one place. Members of the org never
                    see this — their card identity is auto-attached silently. */}
                {policyData && !policyData.is_member && policyData.member_fee_enabled && (
                  <div>
                    <MembershipChoicePanel
                      surcharge={policyData.non_member_surcharge}
                      membershipUrl={policyData.membership_url}
                      allowBuddyGroup={policyData.allow_non_member_buddy_group}
                      organizationId={(policyData as any).organization_id ?? null}
                      choice={
                        formData.non_member_choice === 'pay_non_member' ||
                        formData.non_member_choice === 'claim_member'
                          ? formData.non_member_choice
                          : null
                      }
                      claimedMemberNumber={formData.claimed_member_number}
                      onChoiceChange={(choice) => updateField('non_member_choice', choice ?? '')}
                      onMemberNumberChange={(value) => updateField('claimed_member_number', value)}
                    />
                    {(errors as any).non_member_choice && (
                      <p className="text-xs text-destructive mt-2">{(errors as any).non_member_choice}</p>
                    )}
                    {(errors as any).claimed_member_number && (
                      <p className="text-xs text-destructive mt-2">
                        {(errors as any).claimed_member_number}
                      </p>
                    )}
                  </div>
                )}
                {/* Non-member registrations on orgs without member fees still
                    get a soft notice for buddy-group restrictions, but no
                    surcharge / no three-path picker. */}
                {policyData && !policyData.is_member && !policyData.member_fee_enabled && (
                  <NonMemberNotice
                    surcharge={0}
                    allowBuddyGroup={policyData.allow_non_member_buddy_group}
                  />
                )}
              </div>
              )}

              {/* Right column — fee breakdown & payment selection */}
              <div className="space-y-4">
                {/* Fee breakdown */}
                <div className="rounded-lg border p-3 space-y-1 text-sm">
                  <div className="flex items-center gap-2 mb-2">
                    <DollarSign className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">Fee Breakdown</span>
                  </div>
                  {(event?.entry_fee ?? 0) > 0 && (
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Entry fee</span>
                      <span>{formatCurrency(event?.entry_fee as number)}</span>
                    </div>
                  )}
                  {effectiveTransactionFee > 0 && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">
                          Transaction fee{' '}
                          <span className="text-xs">(non-refundable)</span>
                        </span>
                        <span>{formatCurrency(effectiveTransactionFee)}</span>
                      </div>
                      <p className="text-[10px] text-muted-foreground -mt-0.5">
                        Waived when paying via bank transfer (ACH).
                      </p>
                    </>
                  )}
                  {surcharge > 0 && (
                    <div className="flex justify-between text-amber-600 dark:text-amber-400">
                      <span>Non-member surcharge</span>
                      <span>{formatCurrency(surcharge)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-medium border-t pt-1 mt-1">
                    <span>Total</span>
                    <span>{formatCurrency(totalFee)}</span>
                  </div>
                </div>

                {/* Payment method selection */}
                {(() => {
                  // Pay Later + Cash at Event are members-only perks *when
                  // the rodeo requires membership*. Then non-members must pay
                  // online up-front (Stripe) to hold their spot, and we render
                  // the locked options anyway so the user sees what they're
                  // missing — that's the upgrade pitch. When membership isn't
                  // required, `member_fee_enabled` resolves false, everyone
                  // counts as a member, and nothing is locked. Backend
                  // re-validates on submit either way.
                  const { deferredMembersOnly: membersOnlyLocked } =
                    resolveRegistrationPaymentVisibility(policyData);
                  const membershipUrl =
                    (policyData as any)?.membership_url ?? null;
                  // Open an explainer dialog on click instead of jumping
                  // straight to the org's signup URL. The dialog names the
                  // org and offers a single "Visit <org>" CTA so the user
                  // can choose to leave the registration flow.
                  const handleLockedClick = () => {
                    setMembersOnlyDialogOpen(true);
                  };
                  return (
                <div className="space-y-2">
                  <Label>How would you like to pay?</Label>
                  {/* Pay Later can be switched off per rodeo (with a per-event
                      override). When it's off the card is not rendered at all —
                      not greyed out — because it isn't something membership can
                      unlock. Card/Bank then stands alone, so drop to one column
                      rather than leaving a hole in the grid. */}
                  <div
                    className={`grid gap-2 ${payLaterAllowed ? 'grid-cols-2' : 'grid-cols-1'}`}
                  >
                    {/* Online pay (Stripe) isn't supported for on-behalf
                        registrations yet — the embedded checkout flow is
                        bound to the actor's session and assumes a single
                        registrant. Disable + dim the button so the primary
                        explicitly chooses Cash or Pay Later for the member.
                        The form value 'stripe' is what the backend expects;
                        legacy 'paypal' is also accepted for back-compat. */}
                    <button
                      type="button"
                      disabled={!!formData.target_user_profile_id}
                      onClick={() => updateField('payment_method', 'stripe')}
                      className={`flex items-center gap-2 rounded-lg border p-3 text-left transition-colors ${
                        formData.target_user_profile_id
                          ? 'border-border bg-muted text-muted-foreground cursor-not-allowed opacity-60'
                          : formData.payment_method === 'stripe' ||
                              formData.payment_method === 'paypal'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <CreditCard className="h-5 w-5 text-blue-600 shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm leading-tight">
                          Card or Bank
                        </p>
                        <p className="text-[10px] leading-tight text-muted-foreground mt-0.5">
                          {formData.target_user_profile_id
                            ? 'Not available for on-behalf'
                            : 'Pay now via Stripe (card or ACH)'}
                        </p>
                      </div>
                    </button>
                    {payLaterAllowed && (
                    <button
                      type="button"
                      aria-disabled={membersOnlyLocked || undefined}
                      title={
                        membersOnlyLocked
                          ? 'Members only — become a member to pay later'
                          : undefined
                      }
                      onClick={() => {
                        if (membersOnlyLocked) {
                          handleLockedClick();
                          return;
                        }
                        updateField('payment_method', 'pay_later');
                      }}
                      className={`relative flex items-center gap-2 rounded-lg border p-3 text-left transition-colors ${
                        membersOnlyLocked
                          ? 'border-border bg-muted/40 cursor-not-allowed opacity-70 hover:border-amber-500/40'
                          : formData.payment_method === 'pay_later'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Clock className="h-5 w-5 text-amber-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm leading-tight flex items-center gap-1.5">
                          Pay Later
                          {membersOnlyLocked && (
                            <Lock className="h-3 w-3 text-muted-foreground" aria-hidden />
                          )}
                        </p>
                        <p className="text-[10px] leading-tight text-muted-foreground mt-0.5">
                          {membersOnlyLocked
                            ? 'Members only'
                            : 'Pay online after the draw'}
                        </p>
                      </div>
                    </button>
                    )}
                  </div>
                  {cashAllowed && (
                    <button
                      type="button"
                      aria-disabled={membersOnlyLocked || undefined}
                      title={
                        membersOnlyLocked
                          ? 'Members only — become a member to pay with cash at the event'
                          : undefined
                      }
                      onClick={() => {
                        if (membersOnlyLocked) {
                          handleLockedClick();
                          return;
                        }
                        updateField('payment_method', 'cash');
                      }}
                      className={`relative w-full flex items-center gap-2 rounded-lg border p-3 text-left transition-colors ${
                        membersOnlyLocked
                          ? 'border-border bg-muted/40 cursor-not-allowed opacity-70 hover:border-amber-500/40'
                          : formData.payment_method === 'cash'
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/50'
                      }`}
                    >
                      <Banknote className="h-5 w-5 text-green-600 shrink-0" />
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-sm leading-tight flex items-center gap-1.5">
                          Cash at Event
                          {membersOnlyLocked && (
                            <Lock className="h-3 w-3 text-muted-foreground" aria-hidden />
                          )}
                        </p>
                        <p className="text-[10px] leading-tight text-muted-foreground mt-0.5">
                          {membersOnlyLocked
                            ? 'Members only'
                            : 'Pay in person — your spot is held until event day'}
                        </p>
                      </div>
                    </button>
                  )}
                  {/* Only surface the members-only pitch when at least one
                      deferred option is actually on the screen — naming an
                      option the rodeo has switched off would advertise
                      something membership can't unlock. */}
                  {membersOnlyLocked && (payLaterAllowed || cashAllowed) && (
                    <p className="text-[11px] leading-tight text-muted-foreground italic">
                      {payLaterAllowed && cashAllowed
                        ? 'Pay Later and Cash at Event are reserved for members. '
                        : payLaterAllowed
                          ? 'Pay Later is reserved for members. '
                          : 'Cash at Event is reserved for members. '}
                      {membershipUrl ? (
                        <a
                          href={membershipUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline-offset-2 hover:underline"
                        >
                          Become a member
                        </a>
                      ) : (
                        <span>Contact the organization to become a member</span>
                      )}{' '}
                      {payLaterAllowed && cashAllowed
                        ? 'to unlock these options.'
                        : 'to unlock it.'}
                    </p>
                  )}
                  {errors.payment_method && (
                    <p className="text-xs text-destructive">{errors.payment_method}</p>
                  )}
                </div>
                  );
                })()}

                {/* Pay for partner option. Label uses the partner's
                    actual per-side cost (membership-aware) when their
                    fee preview has loaded — otherwise we fall back to
                    the primary's totalFee so the affordance still
                    renders before the partner has been picked. The
                    member/non-member chip below the checkbox surfaces
                    *whose* membership status drove the number so the
                    user can see at a glance which fees apply to who. */}
                {formData.registration_type === 'team_primary' && (
                  <div className="space-y-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.paid_for_partner}
                        onChange={(e) => updateField('paid_for_partner', e.target.checked)}
                        className="rounded border-border"
                      />
                      <span className="text-sm">
                        Pay for my partner's entry fee (
                        {formatCurrency(partnerTotalFee)})
                      </span>
                    </label>
                    {partnerFeePreview &&
                      formData.paid_for_partner &&
                      partnerFeePreview.member_fee_enabled && (
                        <div className="ml-6 text-xs text-muted-foreground">
                          {partnerFeePreview.partner_display_name ?? 'Partner'}{' '}
                          —{' '}
                          {partnerFeePreview.is_member ? (
                            <span className="text-emerald-600 dark:text-emerald-400">
                              member of{' '}
                              {partnerFeePreview.organization_name ?? 'this org'}{' '}
                              · no non-member surcharge
                            </span>
                          ) : (
                            <span className="text-amber-600 dark:text-amber-400">
                              not a member of{' '}
                              {partnerFeePreview.organization_name ?? 'this org'}{' '}
                              · +
                              {formatCurrency(
                                partnerFeePreview.non_member_surcharge,
                              )}{' '}
                              non-member surcharge
                            </span>
                          )}
                        </div>
                      )}
                  </div>
                )}

                {/* Entry-close time + optional fees-due note. Pulled from
                    the parent rodeo via /registration-policy. We render the
                    block whenever an entry_close_time is present; the
                    message line appears only when the admin set one. Sits
                    at the bottom of the right column so it balances the
                    height of a tall membership-choice panel on the left. */}
                {policyData?.entry_close_time && (
                  <div className="rounded-lg border p-3 text-sm">
                    <div className="font-medium">
                      Entries Close:{' '}
                      {formatInTimezone(
                        policyData.entry_close_time,
                        policyData.timezone || 'America/Los_Angeles',
                        { dateStyle: 'full', timeStyle: 'short' },
                      )}
                    </div>
                    {policyData.fees_due_message && (
                      <div className="text-xs text-muted-foreground mt-1">
                        {policyData.fees_due_message}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            );
          })()}

          {/* Review step — two-column layout when there's something to
              show on the right (PayPal buttons OR a separate fee summary
              for cash / pay_later); collapses to one column for free
              events or on-behalf flows where neither applies. */}
          {currentStep === 'review' && (() => {
            // PayPal column: payment_method=paypal and not on-behalf.
            // Render gates intentionally exclude hasUnpaidBalance /
            // targetAlreadyRegistered so the column stays mounted
            // through the create → approve → capture lifecycle (those
            // flags flip true mid-flow because our own createOrder
            // mutation mints the pending registration row that
            // backfills into the buddy-group + outstanding-balance
            // queries — Step 1's Next gate already prevents users from
            // reaching review with those hard-blocker conditions in
            // the first place).
            // Online-pay column: payment_method is the Stripe online-pay
            // sentinel (stripe, or the legacy 'paypal' value) and we're not
            // on the on-behalf path. Renamed in spirit but kept the
            // local var name to minimise diff churn — every reader of
            // `showPayPalCol` is local to this file.
            const showPayPalCol =
              (formData.payment_method === 'stripe' ||
                formData.payment_method === 'paypal') &&
              !formData.target_user_profile_id;
            // Right-side fee summary: rendered for non-PayPal, non-free
            // flows (cash / pay_later / on-behalf with charges) so the
            // contestant sees the amount due in the right column next
            // to the registration summary.
            const showFeeCol = !showPayPalCol && !isFreeEvent;
            const useTwoColumn = showPayPalCol || showFeeCol;
            // Reusable fee-summary block. Lives under the registration
            // summary on the left when PayPal is taking the right
            // column; otherwise renders in the right column.
            const feeSummaryBlock = !isFreeEvent ? (
              <div className="rounded-lg border p-3 space-y-1 text-sm">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                  <span className="font-medium">Fee Summary</span>
                </div>
                {(event?.entry_fee ?? 0) > 0 && (
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Entry fee</span>
                    <span>{formatCurrency(event?.entry_fee as number)}</span>
                  </div>
                )}
                {effectiveTransactionFee > 0 && (
                  <>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">
                        Transaction fee{' '}
                        <span className="text-xs">(non-refundable)</span>
                      </span>
                      <span>{formatCurrency(effectiveTransactionFee)}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground -mt-0.5">
                      Waived when paying via bank transfer (ACH).
                    </p>
                  </>
                )}
                {surcharge > 0 && (
                  <div className="flex justify-between text-amber-600 dark:text-amber-400">
                    <span>Non-member surcharge</span>
                    <span>{formatCurrency(surcharge)}</span>
                  </div>
                )}
                {/* On-behalf flow: surface the org's non-member surcharge
                    so the primary knows there's an extra fee waiting for
                    the target when they settle the balance. The line is
                    intentionally excluded from the displayed total — the
                    target picks their own membership option (member
                    number / pay non-member fee) at payment time. We
                    look up the target's actual membership status via
                    targetFeePreview so the hint is hidden entirely for
                    members and only rendered for known non-members
                    (suppressed while the lookup is in flight to avoid
                    a flicker). */}
                {formData.target_user_profile_id &&
                  policyData?.member_fee_enabled &&
                  targetFeePreview &&
                  !targetFeePreview.is_member &&
                  (targetFeePreview.non_member_surcharge ?? 0) > 0 && (
                    <div className="flex justify-between text-amber-600 dark:text-amber-400">
                      <span>Non-member fee</span>
                      <span>
                        +{formatCurrency(targetFeePreview.non_member_surcharge)}
                      </span>
                    </div>
                  )}
                <div className="flex justify-between font-medium border-t pt-1 mt-1">
                  <span>Total</span>
                  <span>{formatCurrency(totalFee)}</span>
                </div>
                {formData.paid_for_partner && (
                  <>
                    {/* Partner fee uses the partner's actual per-side
                        cost so the displayed total matches what Stripe
                        will charge. When the partner's membership
                        differs from the primary's we surface a small
                        chip explaining whose fees are doubled vs.
                        single — without it the asymmetry between
                        "Entry × 2" and "Non-member × 1" looked like a
                        bug. */}
                    <div className="flex justify-between text-muted-foreground">
                      <span>Partner fee</span>
                      <span>{formatCurrency(partnerTotalFee)}</span>
                    </div>
                    {partnerFeePreview &&
                      partnerFeePreview.member_fee_enabled &&
                      partnerTotalFee !== totalFee && (
                        <div className="flex justify-between text-xs text-muted-foreground">
                          <span>
                            {partnerFeePreview.partner_display_name ??
                              'Partner'}{' '}
                            —{' '}
                            {partnerFeePreview.is_member
                              ? 'member · no surcharge'
                              : `non-member · +${formatCurrency(
                                  partnerFeePreview.non_member_surcharge,
                                )}`}
                          </span>
                        </div>
                      )}
                    <div className="flex justify-between font-medium border-t pt-1 mt-1">
                      <span>Total due</span>
                      <span>
                        {formatCurrency(totalFee + partnerTotalFee)}
                      </span>
                    </div>
                  </>
                )}
              </div>
            ) : null;
            return (
            <div
              className={`space-y-3 text-sm ${
                useTwoColumn
                  ? 'md:space-y-0 md:grid md:grid-cols-2 md:gap-x-4 md:items-start'
                  : ''
              }`}
            >
              <div className="space-y-3 self-start">
              <div className="grid grid-cols-2 gap-2 rounded-lg border p-3">
                {/* Registrant — surfaced first so the primary can sanity-check
                    they're registering the right person before submitting. */}
                {formData.target_user_profile_id && (
                  <>
                    <div className="text-muted-foreground">Entering</div>
                    <div className="font-medium">
                      {registerableMembers.find(
                        (m) => m.user_profile_id === formData.target_user_profile_id,
                      )?.display_name ?? 'Buddy group member'}
                    </div>
                  </>
                )}
                {isTeamRoping && (
                  <>
                    <div className="text-muted-foreground">Position</div>
                    <div className="font-medium">{formData.position}</div>
                  </>
                )}
                {(partnerDisplayName || formData.partner_email) && (
                  <>
                    <div className="text-muted-foreground">Partner</div>
                    <div className="font-medium">
                      {partnerDisplayName || formData.partner_email}
                      <span className="text-xs text-muted-foreground ml-1">({partnerPosition})</span>
                    </div>
                  </>
                )}
                {handicapCheck && !handicapCheck.needs_handicap && handicapCheck.existing_handicap_value != null && (
                  <>
                    <div className="text-muted-foreground">Handicap</div>
                    <div className="font-medium">{handicapCheck.existing_handicap_value} ({handicapCheck.handicap_type_name})</div>
                  </>
                )}
                {handicapCheck?.needs_handicap && formData.manual_handicap_value != null && (
                  <>
                    <div className="text-muted-foreground">Handicap</div>
                    <div className="font-medium">{formData.manual_handicap_value} <span className="text-xs text-amber-500 dark:text-amber-400">(pending approval)</span></div>
                  </>
                )}
                {formData.first_performance_preference && (() => {
                  const parts = formatPreferenceParts(formData.first_performance_preference, perfDropdownTz);
                  return (
                    <>
                      <div className="text-muted-foreground">1st Preference</div>
                      <div className="font-medium leading-tight">
                        <div>{parts.title}</div>
                        {parts.datetime && (
                          <div className="text-xs font-normal text-muted-foreground">
                            {parts.datetime}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
                {formData.second_performance_preference && (() => {
                  const parts = formatPreferenceParts(formData.second_performance_preference, perfDropdownTz);
                  return (
                    <>
                      <div className="text-muted-foreground">2nd Preference</div>
                      <div className="font-medium leading-tight">
                        <div>{parts.title}</div>
                        {parts.datetime && (
                          <div className="text-xs font-normal text-muted-foreground">
                            {parts.datetime}
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}
                {formData.buddy_group_id && (
                  <>
                    <div className="text-muted-foreground">Buddy Group</div>
                    <div className="font-medium flex items-center gap-1">
                      <Users className="h-3.5 w-3.5" />
                      {buddyGroupForRodeo &&
                      buddyGroupForRodeo.id === formData.buddy_group_id
                        ? buddyGroupForRodeo.group_name || 'Assigned'
                        : 'Assigned'}
                    </div>
                  </>
                )}
                {!isFreeEvent && !formData.target_user_profile_id && (
                  <>
                    <div className="text-muted-foreground">Payment</div>
                    <div className="font-medium">
                      {formData.payment_method === 'stripe' ||
                      formData.payment_method === 'paypal'
                        ? 'Card or Bank (Stripe)'
                        : formData.payment_method === 'pay_later'
                          ? 'Pay Later'
                          : 'Cash at Event'}
                    </div>
                  </>
                )}
                {!isFreeEvent && formData.target_user_profile_id && (
                  <>
                    <div className="text-muted-foreground">Payment</div>
                    <div className="font-medium">Member chooses on payment</div>
                  </>
                )}
                {formData.notes && (
                  <>
                    <div className="text-muted-foreground">Notes</div>
                    <div className="font-medium">{formData.notes}</div>
                  </>
                )}
              </div>

              {/* Fee summary — only rendered in the left column when the
                  right column is taken by the PayPal buttons. Otherwise
                  the fee summary lives in the right column (rendered
                  below) so summary-on-left / amount-on-right reads
                  naturally for cash / pay_later / on-behalf flows. */}
              {showPayPalCol && feeSummaryBlock}

              {/* On-behalf disclaimer — replaces the pay_later/cash notes
                  because the target picks their own payment method (and
                  membership choice) when they actually settle the balance.
                  The spot is held under their account in pending_payment. */}
              {formData.target_user_profile_id ? (
                <p className="text-xs text-amber-500 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                  This holds the spot for the member you selected. They will
                  pick their own payment method and membership option (member
                  number, non-member fee, etc.) when they settle the balance
                  from their account.
                </p>
              ) : (
                <>
                  {formData.payment_method === 'cash' && (
                    <p className="text-xs text-amber-500 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                      Your entry will be held with &quot;pending payment&quot; status until you pay at the event.
                    </p>
                  )}

                  {formData.payment_method === 'pay_later' && (
                    <p className="text-xs text-amber-500 dark:text-amber-400 bg-amber-500/10 border border-amber-500/20 p-2 rounded">
                      Your spot is locked in. Payment is deferred — settle online once the
                      draw resolves so you don&apos;t pay a non-refundable transaction fee
                      if you get drawn out.
                    </p>
                  )}
                </>
              )}

              {submitError && (
                <p className="text-xs text-destructive">{submitError}</p>
              )}
              </div>

              {/* Right column — for online pay (Stripe) we render a
                  trigger button that opens the Stripe checkout dialog
                  (PaymentElement = card + ACH). For cash / pay_later /
                  free flows we render the fee summary instead. */}
              {showPayPalCol ? (
                <div className="space-y-2 self-start">
                  <p className="text-xs text-muted-foreground text-center">
                    Click below to complete your entry with card or
                    bank transfer (ACH).
                  </p>
                  <Button
                    className="w-full h-11"
                    onClick={() => setStripeDialogOpen(true)}
                    disabled={isSubmitting || paypalFlowInFlight}
                  >
                    Pay & Confirm Entry
                  </Button>
                </div>
              ) : showFeeCol ? (
                <div className="space-y-2 self-start">
                  {feeSummaryBlock}
                </div>
              ) : null}
            </div>
            );
          })()}
        </div>
        </div>

        <DialogFooter className="flex gap-2 sm:gap-2 sm:justify-end">
          {currentStepIndex > 0 && (
            <Button
              variant="outline"
              onClick={prevStep}
              // Lock Back while a PayPal session is open — otherwise the
              // user can navigate to a previous step with the embedded
              // card form still mounted in PayPal's iframe, leaving the
              // session stranded and the Submit button (footer) hidden
              // for paypal flow with no way to proceed.
              disabled={isSubmitting || paypalFlowInFlight}
            >
              Back
            </Button>
          )}
          <Button variant="outline" onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          {currentStep === 'review' ? (
            // On-behalf flow: render an explicit Register button — the target
            // picks payment later, so PayPal isn't embedded for them. Other
            // payment methods (cash / pay_later / free) also use this button.
            // PayPal review intentionally has no footer button: the embedded
            // PayPal funding buttons in the review step's left column are the
            // submit affordance. `targetAlreadyRegistered` blocks both self
            // and on-behalf paths so a primary who already has a row can't
            // re-submit by leaving the picker on Myself.
            formData.target_user_profile_id ? (
              <Button
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  targetAlreadyRegistered ||
                  onBehalfWithOutsidePartner
                }
              >
                {isSubmitting
                  ? 'Submitting...'
                  : targetAlreadyRegistered
                    ? 'Already Entered'
                    : 'Enter Member'}
              </Button>
            ) : formData.payment_method === 'stripe' ||
              formData.payment_method === 'paypal' ? null : (
              <Button
                onClick={handleSubmit}
                disabled={isSubmitting || targetAlreadyRegistered}
              >
                {isSubmitting
                  ? 'Submitting...'
                  : targetAlreadyRegistered
                    ? 'Already Entered'
                    : 'Submit Entry'}
              </Button>
            )
          ) : (
            <Button
              onClick={nextStep}
              disabled={targetAlreadyRegistered || onBehalfWithOutsidePartner}
            >
              Next
            </Button>
          )}
        </DialogFooter>
          </>
        )}
      </DialogContent>
      {/* Per-org outstanding-balance blocker. Backend returns 402 with
          the org name + the past-due unpaid registrations for this
          rodeo's organization; the modal lists them and lets the user
          settle the whole batch in one Stripe capture so they can
          retry registration immediately afterward. */}
      <OrgOutstandingBalanceDialog
        open={!!orgUnpaidBlock}
        onClose={clearOrgUnpaidBlock}
        organizationName={orgUnpaidBlock?.organization_name ?? null}
        unpaidEvents={orgUnpaidBlock?.unpaid_events ?? []}
      />

      {/* Stripe checkout dialog — opened from the review step's "Pay &
          Confirm Registration" button. Deferred-mint flow: opens with a
          local payment row (no Stripe PI yet), mounts <PaymentElement />
          in mode:'payment'. The Stripe PI is minted at submit time via
          /mint-intent with the chosen payment method's amount (ACH waives
          the transaction fee). The webhook is the canonical settle path;
          on successful confirmation we POST /complete to flip the
          registration immediately (UI fast path). */}
      <StripeCheckoutDialog
        open={stripeDialogOpen}
        onClose={() => setStripeDialogOpen(false)}
        title="Pay entry fee"
        amountLabel="Amount due"
        fallbackTotal={stripeOrder?.total_amount ?? totalFee}
        // MUST stay stable across the dialog's open lifecycle. Deriving
        // this from the payment_id flips its value the moment the first
        // quote resolves, which retriggers the quote effect and creates
        // a duplicate `pending_payment` registration on every open.
        triggerKey={`mint-${currentStepIndex}`}
        quoteOrder={quoteStripeOrder}
        confirmIntent={confirmStripeIntent}
        availableCredit={eventCredit?.available ?? 0}
        onApprove={handleStripeApprove}
        onCancel={handleStripeCancel}
      />

      {/* Members-only explainer. Surfaced when a non-member clicks one of
          the locked Pay Later / Cash buttons. Names the org so the copy is
          specific, then offers a single CTA to the org's signup page (or
          general website if no signup link is set) plus a Close action. */}
      <Dialog
        open={membersOnlyDialogOpen}
        onOpenChange={setMembersOnlyDialogOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-4 w-4 text-amber-600" />
              Members only
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              Pay Later and Cash at Event are reserved for members of{' '}
              <span className="font-medium">
                {((policyData as any)?.organization_name as string | null) ??
                  'this organization'}
              </span>
              . Non-members must pay online to secure their registration.
            </p>
            {((policyData as any)?.membership_url as string | null) ? (
              <p className="text-xs text-muted-foreground break-all">
                Sign up at{' '}
                <a
                  href={(policyData as any).membership_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary underline-offset-2 hover:underline"
                >
                  {(policyData as any).membership_url}
                </a>
              </p>
            ) : (
              <p className="text-xs text-muted-foreground">
                This organization hasn't published a signup link yet — contact
                them directly to become a member.
              </p>
            )}
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setMembersOnlyDialogOpen(false)}
            >
              Close
            </Button>
            {((policyData as any)?.membership_url as string | null) && (
              <Button
                onClick={() => {
                  window.open(
                    (policyData as any).membership_url,
                    '_blank',
                    'noopener,noreferrer',
                  );
                  setMembersOnlyDialogOpen(false);
                }}
              >
                Visit{' '}
                {((policyData as any)?.organization_name as string | null) ??
                  'organization'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
